import ConfirmDialog from '../components/ConfirmDialog';
import React, { useState, useEffect } from 'react';
import { toast } from '../components/Toast';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import ServiceOrderModal from '../components/ServiceOrderModal';
import ActivateServicesModal from '../components/ActivateServicesModal';
import StopRecurringModal from '../components/StopRecurringModal';
import EditRecurrencePatternModal from '../components/EditRecurrencePatternModal';
import { calculateMonthDates } from '../utils/dateRecurrence';

const STATUS_COLORS_DEV = {
  active:    { color: '#B71C1C', bg: '#ffebee', label: 'Activity' },
  clean:     { color: '#2D5C3F', bg: '#e8f5e9', label: 'Clean' },
  no_access: { color: '#E65100', bg: '#fff3e0', label: 'No Access' },
  exception: { color: '#6A1B9A', bg: '#f3e5f5', label: 'Exception' },
  pending:   { color: '#999',    bg: '#f5f5f5', label: 'Pending' },
};
const NODE_ICONS_DEV = { building: '🏢', unit: '🏗️', area: '📍', device: '🪤' };
const TIER_META = {
  1: { label: 'Z1', full: 'Zone 1 · Food Contact',          bg: '#FEE2E2', color: '#991B1B', icon: '🍽️' },
  2: { label: 'Z2', full: 'Zone 2 · Adjacent / Storage',    bg: '#FED7AA', color: '#9A3412', icon: '📦' },
  3: { label: 'Z3', full: 'Zone 3 · In-Facility Non-Food',  bg: '#FEF3C7', color: '#854D0E', icon: '🏢' },
  4: { label: 'Z4', full: 'Zone 4 · Exterior',              bg: '#E5E7EB', color: '#374151', icon: '🌳' },
};

// Suggestion confidence band → visual style
const BAND_STYLES = {
  high:   { bg: '#DCFCE7', color: '#166534', border: '#86EFAC', label: 'High confidence' },
  medium: { bg: '#FEF9C3', color: '#854D0E', border: '#FDE68A', label: 'Medium confidence' },
  low:    { bg: '#FEE2E2', color: '#991B1B', border: '#FCA5A5', label: 'Needs your input' },
};

const SOURCE_ICONS = {
  keyword_vertical:            '🔬',  // industry-specific equipment
  keyword_universal:           '📋',  // generic keyword match
  keyword_universal_unverified: '⚠️', // keyword but no parent context
  inherited:                   '⬇️',  // from parent
  fallback:                    '❓',  // no signal at all
};
const CHILD_TYPES = { building: ['building','unit','area','device'], unit: ['building','unit','area','device'], area: ['building','unit','area','device'], device: [] };
const DEVICE_TYPES_LIST = [];

const DeviceTreeView = ({ nodes, level, facilityId, onRefresh, deviceTypes, onConfirmDelete, onError, onMoveNode }) => {
  const [editNode, setEditNode] = React.useState(null);
  const [editForm, setEditForm] = React.useState({});
  const [addingTo, setAddingTo] = React.useState(null);
  const [addForm, setAddForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  // Tier suggestion infrastructure
  const [suggestion, setSuggestion] = React.useState(null);          // {tier, fc, confidence, confidence_band, reason, hint, source}
  const [suggestionFor, setSuggestionFor] = React.useState(null);    // 'add' | nodeId being edited
  const [suggestionLoading, setSuggestionLoading] = React.useState(false);
  // Track what was suggested last so we can derive tier_source on save
  const lastSuggestedRef = React.useRef(null);  // { tier, fc, suggestedAt }
  const debounceRef = React.useRef(null);
  // Tracks whether user manually changed tier/fc — prevents future suggestions from clobbering edits.
  // Reset to false on form open and on form close.
  const userOverrodeRef = React.useRef(false);
  // Help modal toggle (lifted up so both Add and Edit can open the same modal)
  const [helpOpen, setHelpOpen] = React.useState(false);
  const [helpData, setHelpData] = React.useState(null);

  const apiCall = async (method, url, body) => {
    const token = localStorage.getItem('token');
    let finalBody = body;
    const writeMethods = ['POST', 'PUT', 'PATCH', 'DELETE'];
    if (writeMethods.includes(method.toUpperCase())) {
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      const HH = String(now.getHours()).padStart(2, '0');
      const MI = String(now.getMinutes()).padStart(2, '0');
      const SS = String(now.getSeconds()).padStart(2, '0');
      const localDate = `${yyyy}-${mm}-${dd}`;
      const localTime = `${HH}:${MI}:${SS}`;
      finalBody = body ? { ...body } : {};
      if (finalBody.local_date === undefined) finalBody.local_date = localDate;
      if (finalBody.local_time === undefined) finalBody.local_time = localTime;
    }
    const res = await fetch('https://api.safecorepest.com/api' + url, {
      method, headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: finalBody ? JSON.stringify(finalBody) : undefined
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Error');
    return data;
  };

  // Debounced call to /tier-suggest. Args: name, node_type, parent_id (optional), formKey ('add' or nodeId).
  const fetchSuggestion = (name, node_type, parent_id, formKey) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!name || !name.trim() || !node_type) {
      setSuggestion(null);
      setSuggestionFor(null);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSuggestionLoading(true);
      try {
        const body = { name: name.trim(), node_type };
        if (parent_id) body.parent_id = parent_id;
        const data = await apiCall('POST', '/facility-structure/tier-suggest', body);
        if (data.success && data.suggestion) {
          const sug = data.suggestion;
          setSuggestion(sug);
          setSuggestionFor(formKey);
          lastSuggestedRef.current = {
            tier: sug.tier,
            fc:   !!sug.is_food_contact,
            suggestedAt: Date.now(),
          };
          // AUTO-APPLY suggestion to the form, but only if user hasn't manually overridden tier/fc yet.
          // This way, after the user types a name, tier+FC are pre-filled. A simple Save = suggested_accepted.
          if (!userOverrodeRef.current && sug.tier != null) {
            const tierVal = String(sug.tier);
            if (formKey === 'add') {
              setAddForm(prev => ({
                ...prev,
                fda_zone_tier: tierVal,
                is_food_contact: !!sug.is_food_contact,
              }));
            } else {
              setEditForm(prev => ({
                ...prev,
                fda_zone_tier: tierVal,
                is_food_contact: !!sug.is_food_contact,
              }));
            }
          }
        } else {
          setSuggestion(null);
          setSuggestionFor(null);
        }
      } catch (err) {
        setSuggestion(null);
        setSuggestionFor(null);
      } finally {
        setSuggestionLoading(false);
      }
    }, 400);
  };

  // Apply suggestion to the form (one-click accept)
  const acceptSuggestion = (formKey) => {
    if (!suggestion) return;
    const tierVal = suggestion.tier == null ? '' : String(suggestion.tier);
    if (formKey === 'add') {
      setAddForm({
        ...addForm,
        fda_zone_tier: tierVal,
        is_food_contact: !!suggestion.is_food_contact,
      });
    } else {
      setEditForm({
        ...editForm,
        fda_zone_tier: tierVal,
        is_food_contact: !!suggestion.is_food_contact,
      });
    }
  };

  // Determine tier_source for an outgoing save based on suggestion history
  const deriveTierSource = (formTier, formFC) => {
    const last = lastSuggestedRef.current;
    if (!last || last.suggestedAt < Date.now() - 5 * 60 * 1000) {
      // No recent suggestion — admin set this manually
      return 'manual';
    }
    const tierMatch = (last.tier == null && (formTier === '' || formTier == null))
                   || (last.tier != null && parseInt(formTier, 10) === last.tier);
    const fcMatch   = !!last.fc === !!formFC;
    if (tierMatch && fcMatch) return 'suggested_accepted';
    return 'suggested_overridden';
  };

  const openHelp = async () => {
    if (helpData) { setHelpOpen(true); return; }
    try {
      const data = await apiCall('GET', '/facility-structure/tier-help', null);
      if (data.success) {
        setHelpData(data.help);
        setHelpOpen(true);
      }
    } catch (e) {
      onError && onError('Could not load tier help: ' + e.message);
    }
  };

  const startEdit = (node) => {
    setEditNode(node.id);
    setEditForm({
      name: node.name,
      barcode: node.barcode || '',
      device_type_id: node.device_type_id || (DEVICE_TYPES_LIST[0] && DEVICE_TYPES_LIST[0].id) || '',
      fda_zone_tier: node.fda_zone_tier == null ? '' : String(node.fda_zone_tier),
      is_food_contact: !!node.is_food_contact,
    });
    setAddingTo(null);
    setSuggestion(null);
    setSuggestionFor(null);
    lastSuggestedRef.current = null;
    // If node already has a tier, treat that as user's authoritative value — don't override
    userOverrodeRef.current = node.fda_zone_tier != null;
  };

  const saveEdit = async (node) => {
    if (!editForm.name.trim()) { onError('Name is required'); return; }
    if (node.node_type === 'device' && !editForm.barcode.trim()) { alert('Barcode is required for devices'); return; }
    setSaving(true);
    try {
      const editTierInt = editForm.fda_zone_tier === '' || editForm.fda_zone_tier == null
                            ? null : parseInt(editForm.fda_zone_tier, 10);
      const editTierSource = editTierInt === null ? null : deriveTierSource(editForm.fda_zone_tier, editForm.is_food_contact);
      await apiCall('PUT', '/facility-structure/nodes/' + node.id, {
        name: editForm.name, barcode: editForm.barcode || null,
        device_type_id: node.node_type === 'device' ? parseInt(editForm.device_type_id) : node.device_type_id,
        node_type: node.node_type, description: node.description,
        fda_zone_tier: editTierInt,
        is_food_contact: !!editForm.is_food_contact,
        tier_source: editTierSource,
      });
      setEditNode(null);
      onRefresh();
    } catch(e) { onError(e.message); }
    setSaving(false);
  };

  const deleteN = (node) => {
    onConfirmDelete(node, async () => {
      try {
        await apiCall('DELETE', '/facility-structure/nodes/' + node.id);
        onRefresh();
      } catch(e) { onError(e.message); }
    });
  };

  const startAdd = (node) => {
    const childTypes = CHILD_TYPES[node.node_type] || [];
    if (childTypes.length === 0) return;
    setAddingTo(node.id);
    setAddForm({
      node_type: childTypes[0],
      name: '', barcode: '', device_type_id: '',
      fda_zone_tier: '', is_food_contact: false,
      _parent_id: node.id,
    });
    setEditNode(null);
    setSuggestion(null);
    setSuggestionFor(null);
    lastSuggestedRef.current = null;
    userOverrodeRef.current = false;  // fresh form, no override yet
  };

  const saveAdd = async (parentNode) => {
    if (!addForm.name.trim()) { onError('Name is required'); return; }
    if (addForm.node_type === 'device' && !(addForm.barcode||'').trim()) { onError('Barcode/QR code is required for devices'); return; }
    if (addForm.node_type === 'device' && !addForm.device_type_id) { onError('Device type is required'); return; }
    setSaving(true);
    try {
      const fid = facilityId || parentNode.facility_id;
      const tierInt = addForm.fda_zone_tier === '' || addForm.fda_zone_tier == null
                        ? null : parseInt(addForm.fda_zone_tier, 10);
      const tierSource = tierInt === null ? null : deriveTierSource(addForm.fda_zone_tier, addForm.is_food_contact);
      await apiCall('POST', '/facility-structure/nodes', {
        facility_id: fid, parent_id: parentNode.id,
        node_type: addForm.node_type, name: addForm.name.trim(),
        barcode: (addForm.barcode || '').trim() || null,
        device_type_id: addForm.node_type === 'device' ? parseInt(addForm.device_type_id) : null,
        fda_zone_tier: tierInt,
        is_food_contact: !!addForm.is_food_contact,
        tier_source: tierSource,
      });
      setAddingTo(null);
      setSuggestion(null);
      setSuggestionFor(null);
      lastSuggestedRef.current = null;
      onRefresh();
    } catch(e) { onError(e.message); }
    setSaving(false);
  };

  const BG = ['#EFF6FF','#F8FAFC','#FAFAFA','transparent'];
  const iStyle = { padding:'4px 8px', borderRadius:6, border:'1px solid #ddd', fontSize:12 };

  // Renders the suggestion panel for a given form context ('add' or nodeId)
  const renderSuggestionPanel = (formKey) => {
    if (suggestionFor !== formKey) return null;
    if (suggestionLoading) {
      return (
        <div style={{ width:'100%', padding:'6px 10px', background:'#F1F5F9', borderRadius:6,
                      fontSize:11, color:'#64748B', fontStyle:'italic' }}>
          ⏳ Analyzing…
        </div>
      );
    }
    if (!suggestion) return null;
    const band = BAND_STYLES[suggestion.confidence_band] || BAND_STYLES.medium;
    const tierLabel = suggestion.tier == null ? 'No tier' : `Z${suggestion.tier}`;
    const tierMeta = suggestion.tier ? TIER_META[suggestion.tier] : null;
    const sourceIcon = SOURCE_ICONS[suggestion.source] || '💡';
    return (
      <div style={{
        width:'100%', padding:'8px 10px', borderRadius:6,
        background: band.bg, color: band.color, border: `1px solid ${band.border}`,
        fontSize:11, marginTop:4,
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
          <span style={{ fontWeight:700 }}>💡 Suggested:</span>
          <span style={{
            padding:'1px 6px', borderRadius:10, fontWeight:700,
            background: tierMeta ? tierMeta.bg : '#fff',
            color: tierMeta ? tierMeta.color : band.color,
          }}>
            {suggestion.is_food_contact ? '🍽️' : ''}{tierLabel}
            {suggestion.is_food_contact ? ' · Food Contact' : ''}
          </span>
          <span style={{ fontSize:10, opacity:0.85 }}>
            {sourceIcon} {suggestion.reason}
          </span>
          <span style={{ marginLeft:'auto', fontSize:10, fontWeight:700 }}>
            {band.label} ({Math.round(suggestion.confidence * 100)}%)
          </span>
        </div>
        {suggestion.hint && (
          <div style={{ marginTop:4, fontSize:10, fontStyle:'italic', opacity:0.9 }}>
            ⚠️ {suggestion.hint}
          </div>
        )}
        <div style={{ marginTop:4, fontSize:10, opacity:0.85, fontStyle:'italic' }}>
          {suggestion.tier != null
            ? (userOverrodeRef.current
                ? '✏️ You overrode the suggestion — your value will be saved.'
                : '✓ Auto-applied to the form. Change the dropdown above to override.')
            : 'No tier auto-applied — please pick one in the dropdown above.'}
        </div>
      </div>
    );
  };

  // Help modal (decision guide, common mistakes, examples)
  const renderHelpModal = () => {
    if (!helpOpen) return null;
    const data = helpData;
    return (
      <div
        onClick={() => setHelpOpen(false)}
        style={{ position:'fixed', top:0, left:0, right:0, bottom:0,
                 background:'rgba(0,0,0,0.5)', zIndex:9999,
                 display:'flex', alignItems:'flex-start', justifyContent:'center',
                 paddingTop:50, paddingBottom:50, overflow:'auto' }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{ background:'#fff', borderRadius:10, maxWidth:760, width:'92%',
                   padding:24, boxShadow:'0 10px 40px rgba(0,0,0,0.3)' }}
        >
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
            <h2 style={{ margin:0, color:'#2D5C3F', fontSize:20 }}>📚 FDA Zone Tier Guide</h2>
            <button onClick={() => setHelpOpen(false)}
              style={{ background:'#f5f5f5', border:'none', borderRadius:6, padding:'5px 10px', cursor:'pointer', fontSize:14 }}>
              ✕ Close
            </button>
          </div>
          {!data && <div style={{ color:'#666', fontStyle:'italic' }}>Loading…</div>}
          {data && (
            <div>
              <section style={{ marginBottom:18 }}>
                <h3 style={{ fontSize:14, color:'#374151', marginBottom:8 }}>Tier Definitions</h3>
                {Object.entries(data.tier_definitions).map(([n, def]) => {
                  const m = TIER_META[parseInt(n,10)];
                  return (
                    <div key={n} style={{
                      padding:10, borderRadius:6, marginBottom:6,
                      background: m ? m.bg : '#f5f5f5',
                      borderLeft: m ? `4px solid ${m.color}` : '4px solid #ccc',
                    }}>
                      <div style={{ fontWeight:700, color: m ? m.color : '#333', marginBottom:3 }}>
                        {def.label}
                      </div>
                      <div style={{ fontSize:12, color:'#444' }}>{def.definition}</div>
                      {def.examples_universal && (
                        <div style={{ fontSize:11, color:'#666', marginTop:4 }}>
                          <strong>Examples:</strong> {def.examples_universal.join(', ')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </section>

              <section style={{ marginBottom:18 }}>
                <h3 style={{ fontSize:14, color:'#374151', marginBottom:8 }}>Decision Guide</h3>
                <ol style={{ paddingLeft:20, margin:0, fontSize:12 }}>
                  {data.decision_guide.map((step, i) => (
                    <li key={i} style={{ marginBottom:8 }}>
                      <div style={{ fontWeight:600 }}>{step.question}</div>
                      <div style={{ color:'#15803D', marginTop:2 }}>✓ Yes → {step.yes}</div>
                      {step.no && <div style={{ color:'#92400E', fontSize:11 }}>✗ No → {step.no}</div>}
                    </li>
                  ))}
                </ol>
              </section>

              <section style={{ marginBottom:18 }}>
                <h3 style={{ fontSize:14, color:'#374151', marginBottom:8 }}>Common Mistakes</h3>
                <ul style={{ paddingLeft:20, margin:0, fontSize:12, color:'#7F1D1D' }}>
                  {data.common_mistakes.map((m, i) => <li key={i} style={{ marginBottom:5 }}>⚠️ {m}</li>)}
                </ul>
              </section>

              <section style={{ marginBottom:18 }}>
                <h3 style={{ fontSize:14, color:'#374151', marginBottom:8 }}>Food Contact — Important</h3>
                <ul style={{ paddingLeft:20, margin:0, fontSize:12 }}>
                  {data.food_contact.map((m, i) => <li key={i} style={{ marginBottom:4 }}>{m}</li>)}
                </ul>
              </section>

              <section>
                <h3 style={{ fontSize:14, color:'#374151', marginBottom:8 }}>Examples by Industry</h3>
                {Object.entries(data.examples).filter(([k]) => k !== '_comment').map(([industry, txt]) => (
                  <div key={industry} style={{ marginBottom:6, fontSize:11, padding:6, background:'#F9FAFB', borderRadius:4 }}>
                    <strong style={{ color:'#374151' }}>{industry.replace(/_/g, ' ')}:</strong> <span style={{ color:'#555' }}>{txt}</span>
                  </div>
                ))}
              </section>

              <div style={{ marginTop:14, fontSize:10, color:'#999', textAlign:'right' }}>
                Knowledge base v{data.version}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const btn = (bg, color, title, onClick) => (
    <button onClick={onClick} title={title} style={{ padding:'3px 8px', background:bg, color, border:'none', borderRadius:6, fontSize:11, fontWeight:700, cursor:'pointer', marginLeft:3 }}>{title}</button>
  );

  return (
    <div style={{ marginLeft: level * 20 }}>
      {renderHelpModal()}
      {nodes.map(node => {
        const canAdd = (CHILD_TYPES[node.node_type] || []).length > 0;
        return (
          <div key={node.id}>
            {editNode === node.id ? (
              <div style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 8px', background:'#FFF9C4', borderRadius:6, marginBottom:3, flexWrap:'wrap' }}>
                <span>{NODE_ICONS_DEV[node.node_type]}</span>
                <input
                  value={editForm.name}
                  onChange={e=>{
                    const v = e.target.value;
                    setEditForm({...editForm, name:v});
                    // Only fetch suggestion if node is currently untagged (don't disrupt tagged nodes)
                    if (!node.fda_zone_tier) {
                      fetchSuggestion(v, node.node_type, node.parent_id, node.id);
                    }
                  }}
                  style={{...iStyle, flex:1, minWidth:100}} placeholder="Name *" />
                <input value={editForm.barcode} onChange={e=>setEditForm({...editForm,barcode:e.target.value})}
                    style={{...iStyle, width:80}} placeholder={node.node_type==='device'?'Barcode *':'Barcode (opt)'} />
                {node.node_type==='device' && <>
                  <select value={editForm.device_type_id} onChange={e=>setEditForm({...editForm,device_type_id:e.target.value})} style={iStyle}>
                    {deviceTypes.map(dt=><option key={dt.id} value={dt.id}>{dt.name}</option>)}
                  </select>
                </>}
                <select
                  value={editForm.fda_zone_tier}
                  onChange={e=>{
                    const v = e.target.value;
                    userOverrodeRef.current = true;
                    setEditForm({
                      ...editForm,
                      fda_zone_tier: v,
                      is_food_contact: v === '1' ? true : (v === '' ? editForm.is_food_contact : false),
                    });
                  }}
                  style={{...iStyle, fontSize:11}}
                  title="FDA Zone Tier"
                >
                  <option value="">— No Tier —</option>
                  <option value="1">Z1 · Food Contact</option>
                  <option value="2">Z2 · Adjacent</option>
                  <option value="3">Z3 · Non-Food</option>
                  <option value="4">Z4 · Exterior</option>
                </select>
                <label style={{ display:'flex', alignItems:'center', gap:3, fontSize:11, color:'#555', cursor:'pointer' }} title="Direct food contact surface">
                  <input
                    type="checkbox"
                    checked={!!editForm.is_food_contact}
                    onChange={e=>{
                      userOverrodeRef.current = true;
                      setEditForm({...editForm,is_food_contact:e.target.checked});
                    }}
                    style={{ margin:0 }}
                  />
                  🍽️ FC
                </label>
                <button onClick={openHelp}
                  style={{ padding:'3px 8px', background:'transparent', color:'#2D5C3F',
                           border:'1px solid #2D5C3F', borderRadius:6, fontSize:11, cursor:'pointer' }}
                  title="Open tier guide">?</button>
                <button onClick={()=>saveEdit(node)} disabled={saving} style={{ padding:'3px 10px', background:'#2D5C3F', color:'#fff', border:'none', borderRadius:6, fontSize:11, fontWeight:700, cursor:'pointer' }}>💾 Save</button>
                <button onClick={()=>{setEditNode(null); setSuggestion(null); setSuggestionFor(null);}} style={{ padding:'3px 10px', background:'#f5f5f5', color:'#666', border:'none', borderRadius:6, fontSize:11, cursor:'pointer' }}>✕</button>
                {renderSuggestionPanel(node.id)}
              </div>
            ) : (
              <div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px', borderRadius:6, marginBottom:3,
                background: BG[Math.min(level, BG.length-1)],
                borderLeft: level===0?'3px solid #1565C0':level===1?'3px solid #93C5FD':'3px solid #e9ecef' }}>
                <span style={{fontSize:15}}>{NODE_ICONS_DEV[node.node_type]||'📌'}</span>
                <span style={{fontSize:13, fontWeight:node.node_type!=='device'?700:400, color:'#1a1a1a', flex:1, display:'flex', alignItems:'center', gap:6, flexWrap:'wrap'}}>
                  <span>{node.name}</span>
                  {node.device_type_name&&<span style={{color:'#888',fontWeight:400}}>· {node.device_type_name}</span>}
                  {node.barcode&&<span style={{color:'#aaa',fontSize:11}}>#{node.barcode}</span>}
                  {node.fda_zone_tier && TIER_META[node.fda_zone_tier] && (
                    <span
                      title={TIER_META[node.fda_zone_tier].full + (node.is_food_contact ? ' · Food Contact' : '')}
                      style={{
                        display:'inline-flex', alignItems:'center', gap:3,
                        padding:'1px 6px', borderRadius:10,
                        background: TIER_META[node.fda_zone_tier].bg,
                        color: TIER_META[node.fda_zone_tier].color,
                        fontSize:10, fontWeight:700, letterSpacing:0.3,
                      }}
                    >
                      {node.is_food_contact ? '🍽️' : ''}{TIER_META[node.fda_zone_tier].label}
                    </span>
                  )}
                  {!node.fda_zone_tier && (node.node_type === 'building' || node.node_type === 'unit' || node.node_type === 'area') && (
                    <span
                      title="No FDA zone tier assigned — click ✏️ to tag"
                      style={{
                        padding:'1px 6px', borderRadius:10,
                        border:'1px dashed #cbd5e1', color:'#94a3b8',
                        fontSize:10, fontWeight:600, letterSpacing:0.3,
                      }}
                    >untagged</span>
                  )}
                </span>
                <div style={{display:'flex',gap:2}}>
                  {canAdd && <button onClick={()=>startAdd(node)} style={{padding:'3px 8px',background:'#E3F2FD',color:'#1565C0',border:'none',borderRadius:6,fontSize:12,fontWeight:700,cursor:'pointer'}} title="Add child">＋</button>}
                  <button onClick={()=>onMoveNode(node)} style={{padding:'3px 8px',background:'#FEF9C3',color:'#92400E',border:'none',borderRadius:6,fontSize:12,cursor:'pointer'}} title="Move">↕️</button>
                  <button onClick={()=>startEdit(node)} style={{padding:'3px 8px',background:'#F3F4F6',color:'#374151',border:'none',borderRadius:6,fontSize:12,cursor:'pointer'}} title="Edit">✏️</button>
                  <button onClick={()=>deleteN(node)} style={{padding:'3px 8px',background:'#FEF2F2',color:'#DC2626',border:'none',borderRadius:6,fontSize:12,cursor:'pointer'}} title="Delete">🗑️</button>
                </div>
              </div>
            )}
            {addingTo === node.id && (
              <div style={{ marginLeft:20, padding:'8px 10px', background:'#F0FFF4', borderRadius:6, marginBottom:6, border:'1px dashed #2D5C3F' }}>
                <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                  <select
                    value={addForm.node_type}
                    onChange={e=>{
                      const v = e.target.value;
                      setAddForm({...addForm, node_type:v});
                      fetchSuggestion(addForm.name, v, node.id, 'add');
                    }}
                    style={iStyle}
                  >
                    {(CHILD_TYPES[node.node_type]||[]).map(t=><option key={t} value={t}>{t}</option>)}
                  </select>
                  <input
                    value={addForm.name}
                    onChange={e=>{
                      const v = e.target.value;
                      setAddForm({...addForm, name:v});
                      fetchSuggestion(v, addForm.node_type, node.id, 'add');
                    }}
                    style={{...iStyle, flex:1, minWidth:100}} placeholder="Name *"
                  />
                  <input
                    value={addForm.barcode}
                    onChange={e=>setAddForm({...addForm, barcode:e.target.value})}
                    style={{...iStyle, width:90}}
                    placeholder={addForm.node_type==='device' ? 'Barcode *' : 'Barcode (opt)'}
                  />
                  {addForm.node_type==='device' && (
                    <select value={addForm.device_type_id} onChange={e=>setAddForm({...addForm,device_type_id:e.target.value})} style={iStyle}>
                      {deviceTypes.map(dt=><option key={dt.id} value={dt.id}>{dt.name}</option>)}
                    </select>
                  )}
                  <select
                    value={addForm.fda_zone_tier || ''}
                    onChange={e=>{
                      const v = e.target.value;
                      userOverrodeRef.current = true;  // user explicitly chose a tier
                      setAddForm({
                        ...addForm,
                        fda_zone_tier: v,
                        is_food_contact: v === '1' ? true : (v === '' ? addForm.is_food_contact : false),
                      });
                    }}
                    style={{...iStyle, fontSize:11}}
                    title="FDA Zone Tier"
                  >
                    <option value="">— No Tier —</option>
                    <option value="1">Z1 · Food Contact</option>
                    <option value="2">Z2 · Adjacent</option>
                    <option value="3">Z3 · Non-Food</option>
                    <option value="4">Z4 · Exterior</option>
                  </select>
                  <label style={{ display:'flex', alignItems:'center', gap:3, fontSize:11, color:'#555', cursor:'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!addForm.is_food_contact}
                      onChange={e=>{
                        userOverrodeRef.current = true;
                        setAddForm({...addForm, is_food_contact:e.target.checked});
                      }}
                      style={{ margin:0 }}
                    />
                    🍽️ FC
                  </label>
                  <button onClick={openHelp}
                    style={{ padding:'4px 8px', background:'transparent', color:'#2D5C3F',
                             border:'1px solid #2D5C3F', borderRadius:6, fontSize:12, cursor:'pointer' }}
                    title="Open tier guide">?</button>
                  <button onClick={()=>saveAdd(node)} disabled={saving}
                    style={{padding:'4px 12px',background:'#2D5C3F',color:'#fff',border:'none',borderRadius:6,fontSize:12,fontWeight:700,cursor:'pointer'}}>
                    Save
                  </button>
                  <button onClick={()=>{setAddingTo(null); setSuggestion(null); setSuggestionFor(null);}}
                    style={{padding:'4px 10px',background:'#f5f5f5',color:'#666',border:'none',borderRadius:6,fontSize:12,cursor:'pointer'}}>
                    ✕
                  </button>
                </div>
                {renderSuggestionPanel('add')}
              </div>
            )}
            {node.children&&node.children.length>0&&(
              <DeviceTreeView nodes={node.children} level={level+1} facilityId={facilityId} onRefresh={onRefresh} deviceTypes={deviceTypes} onConfirmDelete={onConfirmDelete} onError={onError} onMoveNode={onMoveNode}/>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ClientsSearch = ({ clientId, facilityId }) => {
  const navigate = useNavigate();
  // Core state
  const [allClients, setAllClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [serviceOrders, setServiceOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Client CRUD modal
  const [showClientModal, setShowClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm, setClientForm] = useState({ name:'', industry:'', email:'', billing_email:'', phone:'', billing_address:'', billing_city:'', billing_state:'', billing_zip:'', contract_start_date:'', contract_end_date:'' });
  const [billingEmails, setBillingEmails]   = useState([]); // additional billing emails
  const [billingEmailForm, setBillingEmailForm] = useState({ email:'', label:'' });
  const [showBillingEmailForm, setShowBillingEmailForm] = useState(false);
  const [billingEmailEdit, setBillingEmailEdit] = useState(null);

  // Facility CRUD modal
  const [showFacilityModal, setShowFacilityModal] = useState(false);
  const [editingFacility, setEditingFacility] = useState(null);
  const [facilityForm, setFacilityForm] = useState({ name:'', address:'', city:'', state:'', zip_code:'', facility_type:'', square_footage:'', certification_scheme:'', certification_level:'', audit_frequency:'', next_audit_date:'', site_contact_name:'', site_contact_phone:'', site_contact_email:'' });

  // Product modal
  const [showProductModal, setShowProductModal] = useState(false);
  const [masterProducts, setMasterProducts] = useState([]);
  const [facilityProducts, setFacilityProducts] = useState([]);

  // Report Recipients state
  const [recipients, setRecipients] = useState([]);
  // Phase 2: Recurring Services state
  const [recurringServices, setRecurringServices] = useState([]);
  const [showStopRecurringModal, setShowStopRecurringModal] = useState(false);
  const [stopRecurringTarget, setStopRecurringTarget] = useState(null);
  // Phase 3: Edit pattern modal state
  const [showEditPatternModal, setShowEditPatternModal] = useState(false);
  const [editPatternTarget, setEditPatternTarget] = useState(null);

  // Service History state
  const [showHistory, setShowHistory] = useState(true);
  const [showDevices, setShowDevices] = useState(false);
  const [showComplaintLogs, setShowComplaintLogs] = useState(false);
  const [showFacilityDocs, setShowFacilityDocs] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [bulkEmailTo, setBulkEmailTo] = useState('');
  const [bulkEmailSending, setBulkEmailSending] = useState(false);
  const [selectedEmailRecipients, setSelectedEmailRecipients] = useState([]);
  const [extraEmailTo, setExtraEmailTo] = useState('');
  const [complaintLogs, setComplaintLogs] = useState([]);
  const [complaintLogsLoading, setComplaintLogsLoading] = useState(false);
  const [showAddComplaintLog, setShowAddComplaintLog] = useState(false);
  const [editComplaintLog, setEditComplaintLog] = useState(null);
  const [clForm, setClForm] = useState({ name:'', log_type:1, section_id:'', requires_review:true });
  const [facilityStructureFlat, setFacilityStructureFlat] = useState([]);
  const [deviceTree, setDeviceTree] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [deviceTypesList, setDeviceTypesList] = useState([]);
  const [nodeConfirm, setNodeConfirm] = useState(null);
  const [moveNodeData, setMoveNodeData] = useState(null);
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');
  const [reportPreview, setReportPreview] = useState(null);
  const [reportPreviewLoading, setReportPreviewLoading] = useState(false);
  const [reportPreviewTab, setReportPreviewTab] = useState('report');
  const [reportHistory, setReportHistory] = useState([]);
  const [reportHistoryLoading, setReportHistoryLoading] = useState(false);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [showRecipientForm, setShowRecipientForm] = useState(false);
  const [recipientEdit, setRecipientEdit] = useState(null);
  const [recipientForm, setRecipientForm] = useState({ email: '', contact_name: '' });
  const [recipientSaving, setRecipientSaving] = useState(false);
  const [searchMaster, setSearchMaster] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [productsLoading, setProductsLoading] = useState(false);
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({ name:'', epa_number:'', active_ingredient:'', category:'insecticide', manufacturer:'', formulation:'' });

  // Service Order modal
  const [showSOModal, setShowSOModal] = useState(false);
  const [activateProposal, setActivateProposal] = useState(null);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [facilityDocs, setFacilityDocs] = useState([]);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [showCreateChoice, setShowCreateChoice] = useState(false);
  const [uploadingScanned, setUploadingScanned] = useState(false);
  const [showScannedConfirm, setShowScannedConfirm] = useState(false);
  const [signatureDate, setSignatureDate] = useState('');
  const [scannedProposal, setScannedProposal] = useState(null);
  const [docBlobUrl, setDocBlobUrl] = useState(null);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // ===== DATA LOADING =====
  useEffect(() => { fetchAllClients(); }, []);
  useEffect(() => { if (clientId && allClients.length > 0) { const c = allClients.find(x => x.id === parseInt(clientId)); if (c) loadClient(c); } }, [clientId, allClients]);
  useEffect(() => { if (facilityId && facilities.length > 0) { const f = facilities.find(x => x.id === parseInt(facilityId)); if (f) loadFacility(f); } }, [facilityId, facilities]);

  const fetchAllClients = async () => {
    try { const r = await api.get('/clients'); setAllClients(r.data.data || []); } 
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const loadClient = async (client) => {
    setSelectedClient(client);
    setBillingEmails([]);
    if (client?.id) api.get(`/clients/${client.id}/billing-emails`).then(r => setBillingEmails(r.data.data||[])).catch(()=>setBillingEmails([]));
    setSelectedFacility(null);
    setServiceOrders([]);
    try { const r = await api.get(`/facilities?client_id=${client.id}`); setFacilities(r.data.data || []); }
    catch (e) { console.error(e); }
  };

  const loadFacility = async (facility) => {
    setSelectedFacility(facility);
    // Reset all facility-specific sections
    setShowDevices(false);
    setDeviceTree([]);
    setShowComplaintLogs(false);
    setComplaintLogs([]);
    setShowFacilityDocs(false);
    setSelectedDocIds([]);
    setShowBulkEmail(false);
    setFacilityStructureFlat([]);
    try { const r = await api.get(`/service-orders?facility_id=${facility.id}`); setServiceOrders(r.data || []); }
    catch (e) { console.error(e); }
    // Phase 2: load recurring services for this facility
    try { const r = await api.get(`/recurring?facility_id=${facility.id}`); setRecurringServices(r.data || []); }
    catch (e) { console.error(e); setRecurringServices([]); }
    try { const rr = await api.get(`/facility-structure/facility-emails/${facility.id}`); setRecipients(rr.data.data || []); } catch { setRecipients([]); }
    try { const r = await api.get(`/facilities/${facility.id}/products`); setFacilityProducts(r.data.data || []); }
    catch (e) { console.error(e); }
    try { const r = await api.get('/facility-docs/' + facility.id + '/documents'); setFacilityDocs(r.data.data || []); }
    catch (e) { console.error(e); }
  };

  // ===== CLIENT CRUD =====
  const openCreateClient = () => {
    setEditingClient(null);
    setScannedProposal(null);
    setShowCreateChoice(true);
  };

  const startFromScratch = () => {
    setShowCreateChoice(false);
    setClientForm({ name:'', industry:'', email:'', billing_email:'', phone:'', billing_address:'', billing_city:'', billing_state:'', billing_zip:'', contract_start_date:'', contract_end_date:'' });
    setBillingEmails([]);
    setShowBillingEmailForm(false);
    setShowClientModal(true);
  };

  const handleScannedUpload = async (file) => {
    if (!file) return;
    setUploadingScanned(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const r = await api.post('/service-agreements/parse-scanned', { pdf_data: reader.result });
        const p = r.data.proposal;
        setScannedProposal(p);
        setShowCreateChoice(false);
        // Get today's date as default signature date
        const today = new Date();
        const localDate = today.getFullYear() + '-' + String(today.getMonth()+1).padStart(2,'0') + '-' + String(today.getDate()).padStart(2,'0');
        setSignatureDate(localDate);
        setClientForm({
          name: p.client_name || '',
          industry: p.industry_type || '',
          email: p.signer_email || p.client_email || '',
          phone: p.signer_phone || '',
          billing_address: p.billing_address || p.facility_address || '',
          billing_city: p.facility_city || '',
          billing_state: p.facility_state || '',
          billing_zip: p.facility_zip || '',
          contract_start_date: p.start_date ? p.start_date.substring(0,10) : localDate,
          contract_end_date: ''
        });
        setShowScannedConfirm(true);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Error processing PDF');
      }
      setUploadingScanned(false);
    };
    reader.readAsDataURL(file);
  };

  const handleConfirmScanned = async () => {
    if (!scannedProposal) return;
    setUploadingScanned(true);
    try {
      const p = scannedProposal;

      // 1. Create client automatically
      const clientRes = await api.post('/clients', {
        name: p.client_name || '',
        industry: p.industry_type || '',
        email: p.signer_email || p.client_email || '',
        phone: p.signer_phone || '',
        billing_address: p.billing_address || p.facility_address || '',
        billing_city: p.facility_city || '',
        billing_state: p.facility_state || '',
        billing_zip: p.facility_zip || '',
        contract_start_date: signatureDate || (p.start_date ? p.start_date.substring(0,10) : null) || null,
        contract_end_date: null
      });
      const newClient = clientRes.data.data || clientRes.data;

      // 2. Create facility automatically
      let facilityId = null;
      try {
        const facRes = await api.post('/facilities', {
          client_id: newClient.id,
          name: p.facility_name || p.client_name || 'Main Facility',
          address: p.facility_address || '',
          city: p.facility_city || '',
          state: p.facility_state || '',
          zip_code: p.facility_zip || '',
          site_contact_name: p.site_contact_name || '',
          site_contact_phone: p.site_contact_phone || '',
          certification_scheme: p.certification_scheme || '',
        });
        facilityId = (facRes.data.data || facRes.data).id;
      } catch(e) { console.error('Facility create error:', e); }

      // 3. Link proposal, set active, update signed_at
      await api.put('/service-agreements/' + p.id, {
        client_id: newClient.id,
        facility_id: facilityId,
        status: 'active',
        signed_at: signatureDate ? new Date(signatureDate + 'T12:00:00').toISOString() : new Date().toISOString()
      });

      // 4. PDF auto-attached by backend when agreement is linked

      // 5. Refresh and navigate to new client
      await fetchAllClients();
      setShowScannedConfirm(false);
      setScannedProposal(null);
      setUploadingScanned(false);
      // Navigate to new client detail
      setSelectedClient(newClient);
      // Show activate services modal if proposal has a service
      if (p && p.frequency && p.frequency !== 'one_time' && facilityId) {
        setActivateProposal({ ...p, facility_id: facilityId });
        setShowActivateModal(true);
      }
      navigate('/clients/' + newClient.id);

    } catch(e) {
      console.error('Auto-create error:', e);
      toast.error('Error creating client: ' + (e.response?.data?.message || e.message));
      setUploadingScanned(false);
    }
  };

  const openEditClient = () => {
    if (!selectedClient) return;
    setEditingClient(selectedClient);
    // Load existing billing emails
    api.get(`/clients/${selectedClient.id}/billing-emails`)
      .then(r => setBillingEmails(r.data.data || []))
      .catch(() => setBillingEmails([]));
    setClientForm({
      name: selectedClient.name||'', industry: selectedClient.industry||'', email: selectedClient.email||'', billing_email: selectedClient.billing_email||'',
      phone: selectedClient.phone||'', billing_address: selectedClient.billing_address||'',
      billing_city: selectedClient.billing_city||'', billing_state: selectedClient.billing_state||'',
      billing_zip: selectedClient.billing_zip||'', contract_start_date: selectedClient.contract_start_date ? selectedClient.contract_start_date.substring(0,10) : '',
      contract_end_date: selectedClient.contract_end_date ? selectedClient.contract_end_date.substring(0,10) : ''
    });
    setShowClientModal(true);
  };

  const saveClient = async () => {
    if (!clientForm.name) { toast.error('Client name is required'); return; }
    let newClientData = null;
    try {
      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, clientForm);
        setSelectedClient({...selectedClient, ...clientForm});
      } else {
        const clientRes = await api.post('/clients', clientForm);
        const newClient = clientRes.data.data || clientRes.data;
        newClientData = newClient;
        if (scannedProposal) {
          try {
            let facilityId = null;
            if (scannedProposal.facility_name) {
              const facRes = await api.post('/facilities', {
                client_id: newClient.id, name: scannedProposal.facility_name,
                address: scannedProposal.facility_address || '', city: scannedProposal.facility_city || '',
                state: scannedProposal.facility_state || '', zip_code: scannedProposal.facility_zip || '',
                site_contact_name: scannedProposal.site_contact_name || '',
                site_contact_phone: scannedProposal.site_contact_phone || '',
                certification_scheme: scannedProposal.certification_scheme || '',
              });
              facilityId = (facRes.data.data || facRes.data).id;
            }
            await api.put('/service-agreements/' + scannedProposal.id, {
              client_id: newClient.id, facility_id: facilityId, status: 'active'
            });
            if (scannedProposal.frequency && scannedProposal.frequency !== 'one_time' && facilityId) {
              setActivateProposal({ ...scannedProposal, facility_id: facilityId });
              setShowActivateModal(true);
            }
          } catch (linkErr) { console.error('Link error:', linkErr); }
          setScannedProposal(null);
        }
      }
      // Save additional billing emails
      const savedClientId = editingClient ? editingClient.id : (newClientData?.id || null);
      if (billingEmails.length > 0 && savedClientId) {
        // Get existing billing emails to sync
        try {
          const existing = await api.get(`/clients/${savedClientId}/billing-emails`);
          const existingIds = (existing.data.data || []).map(e => e.id);
          // Add new ones (those with Date.now() as id — not real DB ids)
          for (const be of billingEmails) {
            if (!existingIds.includes(be.id)) {
              await api.post(`/clients/${savedClientId}/billing-emails`, { email: be.email, label: be.label });
            } else {
              await api.put(`/clients/${savedClientId}/billing-emails/${be.id}`, { email: be.email, label: be.label });
            }
          }
          // Remove deleted ones
          for (const eid of existingIds) {
            if (!billingEmails.find(b => b.id === eid)) {
              await api.delete(`/clients/${savedClientId}/billing-emails/${eid}`);
            }
          }
        } catch(beErr) { console.error('billing emails sync error:', beErr); }
      }
      await fetchAllClients();
      setShowClientModal(false);
      if (!editingClient) toast.success('Client created successfully');
    } catch (e) { console.error(e); toast.error('Error saving client'); }
  };

  const deleteClient = async () => {
    if (!selectedClient) return;
    if (facilities.length > 0) { toast.error('Cannot delete client with active facilities. Remove all facilities first.'); return; }
    setConfirmDialog({ message: `Delete client "${selectedClient.name}"? This cannot be undone.`, confirmText: 'Delete Client', icon: '🗑', onConfirm: async () => {
      setConfirmDialog(null);
      try {
        await api.delete(`/clients/${selectedClient.id}`);
        setSelectedClient(null);
        setFacilities([]);
        navigate('/clients');
        fetchAllClients();
      } catch (e) { console.error(e); toast.error('Error deleting client'); }
    }});
  };

  // ===== FACILITY CRUD =====
  const openCreateFacility = () => {
    setEditingFacility(null);
    setFacilityForm({ name:'', address:'', city:'', state:'', zip_code:'', facility_type:'', square_footage:'', certification_scheme:'', certification_level:'', audit_frequency:'', next_audit_date:'', site_contact_name:'', site_contact_phone:'', site_contact_email:'' });
    setShowFacilityModal(true);
  };

  const openEditFacility = () => {
    if (!selectedFacility) return;
    setEditingFacility(selectedFacility);
    setFacilityForm({
      name: selectedFacility.name||'', address: selectedFacility.address||'', city: selectedFacility.city||'',
      state: selectedFacility.state||'', zip_code: selectedFacility.zip_code||'',
      facility_type: selectedFacility.facility_type||'', square_footage: selectedFacility.square_footage||'',
      certification_scheme: selectedFacility.certification_scheme||'', certification_level: selectedFacility.certification_level||'',
      audit_frequency: selectedFacility.audit_frequency||'',
      next_audit_date: selectedFacility.next_audit_date ? selectedFacility.next_audit_date.substring(0,10) : '',
      site_contact_name: selectedFacility.site_contact_name||'', site_contact_phone: selectedFacility.site_contact_phone||'',
      site_contact_email: selectedFacility.site_contact_email||''
    });
    setShowFacilityModal(true);
  };

  const saveFacility = async () => {
    if (!facilityForm.name || !facilityForm.address) { toast.error('Facility name and address are required'); return; }
    try {
      if (editingFacility) {
        await api.put(`/facilities/${editingFacility.id}`, facilityForm);
        setSelectedFacility({...selectedFacility, ...facilityForm});
      } else {
        await api.post('/facilities', { ...facilityForm, client_id: selectedClient.id });
      }
      const r = await api.get(`/facilities?client_id=${selectedClient.id}`);
      setFacilities(r.data.data || []);
      setShowFacilityModal(false);
    } catch (e) { console.error(e); toast.error('Error saving facility'); }
  };

  const deleteFacility = async () => {
    if (!selectedFacility) return;
    const woCount = serviceOrders.length;
    const msg = woCount > 0
      ? `⚠️ Facility "${selectedFacility.name}" has ${woCount} service order(s).\n\nDeleting will permanently remove the facility AND all its service orders, freeing up technician schedules. This cannot be undone.`
      : `Delete facility "${selectedFacility.name}"? This cannot be undone.`;
    setConfirmDialog({
      message: msg,
      confirmText: woCount > 0 ? `Delete Facility + ${woCount} WO(s)` : 'Delete Facility',
      confirmColor: '#DC2626',
      icon: '🗑️',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.delete(`/facilities/${selectedFacility.id}?force=true`);
          setSelectedFacility(null);
          setServiceOrders([]);
          navigate(`/clients/${selectedClient.id}`);
          const r = await api.get(`/facilities?client_id=${selectedClient.id}`);
          setFacilities(r.data.data || []);
          toast.success('Facility deleted successfully');
        } catch (e) { console.error(e); toast.error('Error deleting facility'); }
      }
    });
  };

  // ===== PRODUCT MANAGEMENT =====
  const openProductModal = async () => {
    setShowProductModal(true); setProductsLoading(true); setSearchMaster(''); setCategoryFilter('all');
    try {
      const mRes = await api.get('/products/master');
      console.log('Master products loaded:', mRes.data.data?.length);
      setMasterProducts(mRes.data.data || []);
    } catch (e) { console.error('Master products error:', e); }
    try {
      const fRes = await api.get('/facilities/' + selectedFacility.id + '/products');
      console.log('Facility products loaded:', fRes.data.data?.length);
      setFacilityProducts(fRes.data.data || []);
    } catch (e) { console.error('Facility products error:', e); }
    setProductsLoading(false);
  };

  const addProduct = async (p) => { try { await api.post(`/facilities/${selectedFacility.id}/products`, { product_ids: [p.id] }); setFacilityProducts(prev => [...prev, {...p, product_id: p.id}]); } catch(e){console.error(e);} };
  const removeProduct = async (p) => { try { await api.delete(`/facilities/${selectedFacility.id}/products/${p.product_id||p.id}`); setFacilityProducts(prev => prev.filter(x => (x.product_id||x.id) !== (p.product_id||p.id))); } catch(e){console.error(e);} };
  const approveAll = async () => { try { await api.post(`/facilities/${selectedFacility.id}/products`, {product_ids: masterProducts.map(p=>p.id)}); const r = await api.get(`/facilities/${selectedFacility.id}/products`); setFacilityProducts(r.data.data); } catch(e){console.error(e);} };
  const removeAll = async () => { try { for(const p of facilityProducts) await api.delete(`/facilities/${selectedFacility.id}/products/${p.product_id||p.id}`); setFacilityProducts([]); } catch(e){console.error(e);} };
  const createProduct = async () => { if(!newProduct.name) { toast.error('Name required'); return; } try { const r = await api.post('/products/master', newProduct); setMasterProducts(prev=>[...prev, r.data.data]); setNewProduct({name:'',epa_number:'',active_ingredient:'',category:'insecticide',manufacturer:'',formulation:''}); setShowNewProduct(false); } catch(e){toast.error('Error');} };

  // ── Report Recipients CRUD ──────────────────────────────────────────────────
  const saveRecipient = async () => {
    if (!recipientForm.email || !recipientForm.email.includes('@')) { toast.error('Valid email required'); return; }
    setRecipientSaving(true);
    try {
      if (recipientEdit) {
        await api.put(`/facility-structure/facility-emails/${recipientEdit.id}`, recipientForm);
        toast.success('Recipient updated');
      } else {
        await api.post(`/facility-structure/facility-emails/${selectedFacility.id}`, recipientForm);
        toast.success('Recipient added');
      }
      const res = await api.get(`/facility-structure/facility-emails/${selectedFacility.id}`);
      setRecipients(res.data.data || []);
      setShowRecipientForm(false);
      setRecipientEdit(null);
      setRecipientForm({ email: '', contact_name: '' });
    } catch (err) { toast.error(err.response?.data?.message || 'Error saving recipient'); }
    finally { setRecipientSaving(false); }
  };

  const deleteRecipient = async (rec) => {
    setConfirmDialog({
      message: `Remove ${rec.email} from report recipients?`,
      confirmText: 'Remove', confirmColor: '#DC2626', icon: '🗑️',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.delete(`/facility-structure/facility-emails/${rec.id}`);
          const res = await api.get(`/facility-structure/facility-emails/${selectedFacility.id}`);
          setRecipients(res.data.data || []);
          toast.success('Recipient removed');
        } catch { toast.error('Error removing recipient'); }
      }
    });
  };

  const fetchDeviceTree = async (facilityId) => {
    setDevicesLoading(true);
    try {
      const [strRes, dtRes] = await Promise.all([
        api.get(`/facility-structure/structure/${facilityId}`),
        api.get('/device-types')
      ]);
      const d = strRes.data || strRes;
      setDeviceTree(d.structure || []);
      const dt = dtRes.data || dtRes;
      setDeviceTypesList(Array.isArray(dt) ? dt : (dt.data || []));
    } catch (e) { console.error(e); }
    setDevicesLoading(false);
  };

  // ── Complaint Log helpers ──────────────────────────────────────────────────
  const fetchComplaintLogs = async (facilityId) => {
    setComplaintLogsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`https://api.safecorepest.com/api/complaint-logs/facility/${facilityId}`,
        { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setComplaintLogs(Array.isArray(data) ? data : []);
      // Also load flat structure for section selector
      const sr = await fetch(`https://api.safecorepest.com/api/facility-structure/structure/${facilityId}`,
        { headers: { Authorization: `Bearer ${token}` } });
      const sd = await sr.json();
      const flatten = (nodes, arr=[]) => { nodes.forEach(n => { arr.push(n); flatten(n.children||[], arr); }); return arr; };
      setFacilityStructureFlat(flatten(Array.isArray(sd) ? sd : []));
    } catch(e) { console.error(e); }
    setComplaintLogsLoading(false);
  };

  const saveComplaintLog = async () => {
    const token = localStorage.getItem('token');
    // Enforce Type 1 uniqueness
    if (!editComplaintLog && clForm.log_type === 1 && complaintLogs.length > 0)
      return alert('This facility already has complaint logs. Delete them first before adding a Type 1 log.');
    if (!editComplaintLog && clForm.log_type !== 1 && complaintLogs.some(l => l.log_type === 1))
      return alert('This facility has a Type 1 log. Delete it first before adding sectional logs.');

    try {
      if (editComplaintLog) {
        // Single update
        const finalName = clForm.name.trim();
        if (!finalName) return alert('Log name is required');
        await fetch(`https://api.safecorepest.com/api/complaint-logs/${editComplaintLog.id}`,
          { method:'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type':'application/json' },
            body: JSON.stringify({ name:finalName, log_type:clForm.log_type, section_id:clForm.section_id||null, requires_review:clForm.requires_review, is_active:true }) });
      } else if (clForm.log_type === 1) {
        // Single Type 1
        await fetch(`https://api.safecorepest.com/api/complaint-logs/facility/${selectedFacility.id}`,
          { method:'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type':'application/json' },
            body: JSON.stringify({ name:'General Pest Sighting Log', log_type:1, section_id:null, requires_review:clForm.requires_review, is_active:true }) });
      } else {
        // Bulk create Type 2/3
        const names = (clForm.sectionNames||[]).slice(0, clForm.sectionCount||1);
        if (names.some(n => !n.trim())) return alert('Please name all sections');
        for (const name of names) {
          await fetch(`https://api.safecorepest.com/api/complaint-logs/facility/${selectedFacility.id}`,
            { method:'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type':'application/json' },
              body: JSON.stringify({ name:name.trim(), log_type:clForm.log_type, section_id:clForm.section_id||null, requires_review:clForm.requires_review, is_active:true }) });
        }
      }
      setShowAddComplaintLog(false);
      setEditComplaintLog(null);
      setClForm({ name:'', log_type:1, section_id:'', requires_review:true, sectionCount:1, sectionNames:[''] });
      await fetchComplaintLogs(selectedFacility.id);
      setShowComplaintLogs(true);
    } catch(e) { alert('Error saving complaint log'); }
  };

  const deleteComplaintLog = (id, name) => {
    setConfirmDialog({
      message: `Delete complaint log "${name}"? This cannot be undone.`,
      confirmText: 'Delete Log',
      icon: '🗑',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          const token = localStorage.getItem('token');
          await fetch(`https://api.safecorepest.com/api/complaint-logs/${id}`,
            { method:'DELETE', headers: { Authorization: `Bearer ${token}` } });
          fetchComplaintLogs(selectedFacility.id);
        } catch(e) { alert('Error deleting'); }
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  // ── Service History helpers ─────────────────────────────────────────────────
  const calcDuration = (start, end) => {
    if (!start || !end) return '—';
    const mins = Math.round((new Date(end) - new Date(start)) / 60000);
    if (mins < 0) return '—';
    const h = Math.floor(mins / 60), m = mins % 60;
    return h > 0 ? `${h}h ${m.toString().padStart(2,'0')}m` : `${m}m`;
  };

  const openReportPreview = async (order) => {
    setReportPreviewTab('report');
    setReportHistory([]);
    setReportPreviewLoading(true);
    setReportPreview({ order, data: null, pdfUrl: null });
    try {
      if (order.pdf_filename) {
        setReportPreview({ order, data: null, pdfUrl: `https://api.safecorepest.com/uploads/reports/${encodeURIComponent(order.pdf_filename)}` });
      } else {
        const token = localStorage.getItem('token');
        const res = await fetch(`https://api.safecorepest.com/api/facility-structure/report/${order.id}/pdf`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        setReportPreview({ order, data: null, pdfUrl: blobUrl, blobUrl });
      }
    } catch(e) { toast.error('Could not load report'); setReportPreview(null); }
    setReportPreviewLoading(false);
  };

  const completedOrders = serviceOrders
    .filter(o => o.status === 'completed')
    .filter(o => {
      if (historyDateFrom && o.scheduled_date < historyDateFrom) return false;
      if (historyDateTo && o.scheduled_date > historyDateTo) return false;
      return true;
    })
    .sort((a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date));

  const pendingOrders = serviceOrders.filter(o => o.status !== 'completed')
    .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));

  // Sub-session D Step 14: split orders into Contract Base vs Upgrades & Punctual
  // - Base: WOs from a recurring service that is NOT an upgrade
  // - Extra: WOs from upgrade recurrings + service_punctual + equipment_sale
  const isContractBase = (o) => o.recurring_service_id && o.recurring_is_upgrade !== true;
  const pendingBase    = pendingOrders.filter(isContractBase);
  const pendingExtra   = pendingOrders.filter(o => !isContractBase(o));
  const completedBase  = completedOrders.filter(isContractBase);
  const completedExtra = completedOrders.filter(o => !isContractBase(o));

  const approvedIds = new Set(facilityProducts.map(p => p.product_id||p.id));
  const filteredMaster = masterProducts.filter(p => { if(approvedIds.has(p.id)) return false; if(categoryFilter!=='all'&&p.category!==categoryFilter) return false; if(searchMaster){const s=searchMaster.toLowerCase(); return (p.name||'').toLowerCase().includes(s)||(p.epa_number||'').toLowerCase().includes(s)||(p.active_ingredient||'').toLowerCase().includes(s);} return true; });
  const pCategories = [...new Set(masterProducts.map(p=>p.category).filter(Boolean))].sort();

  // ===== HELPERS =====
  // Sub-session D Step 19: format YMD as "May 12, 2026 (Mon)" for date+day visibility
  const fmtDateDay = (ymd) => {
    if (!ymd) return '—';
    const d = new Date(ymd + 'T00:00:00');
    if (isNaN(d.getTime())) return ymd;
    const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' (' + dayName + ')';
  };
  // Sub-session D Step 20: format HH:MM:SS as "9:00 AM"
  const fmtTime = (timeStr) => {
    if (!timeStr) return '';
    // timeStr is like "09:00:00" or "09:00"
    const parts = String(timeStr).split(':');
    if (parts.length < 2) return timeStr;
    let h = parseInt(parts[0], 10);
    const m = parts[1];
    if (isNaN(h)) return timeStr;
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    if (h === 0) h = 12;
    return h + ':' + m + ' ' + ampm;
  };
  // Phase 2: format recurrence pattern + config legibly
  const formatPattern = (pattern, config) => {
    if (!pattern) return '—';
    const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const c = config || {};
    switch (pattern) {
      case 'daily': {
        const days = (c.days_of_week || []).map(d => dayNames[d]).join(', ');
        return `Daily · ${days || 'Mon-Fri'}`;
      }
      case '2x_week':
        return `2× Week · ${(c.days_of_week || []).map(d => dayNames[d]).join(', ')}`;
      case '3x_week':
        return `3× Week · ${(c.days_of_week || []).map(d => dayNames[d]).join(', ')}`;
      case 'weekly':
        return `Weekly · ${dayNames[c.day_of_week] || '?'}`;
      case 'biweekly': {
        const weeks = (c.weeks || []).join(' & ');
        return `Bi-weekly · ${dayNames[c.day_of_week] || '?'} (weeks ${weeks || '?'})`;
      }
      case 'monthly':
        return `Monthly · Week ${c.week_of_month || '?'} ${dayNames[c.day_of_week] || '?'}`;
      case 'bimonthly':  return `Bi-monthly · Day ${c.day_of_month || 1}`;
      case 'quarterly':  return `Quarterly · Day ${c.day_of_month || 1}`;
      case 'semiannual': return `Semi-annual · Day ${c.day_of_month || 1}`;
      case 'annual':     return `Annual · Day ${c.day_of_month || 1}`;
      default: return pattern;
    }
  };
  // Phase 2: compute Last/Next dates per recurring service
  const getRecurringStats = (rs) => {
    const myWos = serviceOrders.filter(wo => wo.recurring_service_id === rs.id);
    const today = new Date().toISOString().slice(0, 10);
    const completed = myWos.filter(wo => wo.status === 'completed')
      .sort((a, b) => String(b.scheduled_date).localeCompare(String(a.scheduled_date)));
    const lastService = completed[0]?.scheduled_date ? String(completed[0].scheduled_date).slice(0, 10) : null;
    const future = myWos.filter(wo =>
      (wo.status === 'scheduled' || wo.status === 'in_progress') &&
      String(wo.scheduled_date).slice(0, 10) >= today
    ).sort((a, b) => String(a.scheduled_date).localeCompare(String(b.scheduled_date)));
    let nextService = future[0]?.scheduled_date ? String(future[0].scheduled_date).slice(0, 10) : null;
    let isComputed = false;
    if (!nextService && rs.is_active) {
      const startDate = rs.start_date ? String(rs.start_date).slice(0, 10) : null;
      if (startDate) {
        try {
          const startDateObj = new Date(startDate + 'T12:00:00');
          const todayObj = new Date(today + 'T12:00:00');
          const probeStart = startDateObj > todayObj ? startDateObj : todayObj;
          const config = { ...(rs.recurrence_config || {}) };
          if (rs.recurrence_pattern === 'biweekly' && !config.weeks && config.biweekly_option) {
            config.weeks = config.biweekly_option === '1_3' ? [1, 3] : [2, 4];
          }
          for (let off = 0; off < 24; off++) {
            const py = probeStart.getFullYear() + Math.floor((probeStart.getMonth() + off) / 12);
            const pm = (probeStart.getMonth() + off) % 12;
            const dates = calculateMonthDates(rs.recurrence_pattern, config, py, pm, startDate);
            const found = dates.find(d => {
              const dStr = (d.date instanceof Date ? d.date : new Date(d.date)).toISOString().slice(0, 10);
              return dStr >= today && !d.is_extra;
            });
            if (found) {
              nextService = (found.date instanceof Date ? found.date : new Date(found.date)).toISOString().slice(0, 10);
              isComputed = true;
              break;
            }
          }
        } catch (e) { console.error('Phase 2 next-computation error:', e); }
      }
    }
    return { lastService, nextService, isComputed };
  };
  const getCatColor = (c) => ({insecticide:'#2196F3',rodenticide:'#F44336',trap:'#FF9800',glue_board:'#9C27B0',dust:'#795548',granular:'#4CAF50',igr:'#00BCD4',aerosol:'#607D8B'}[c]||'#9E9E9E');
  const getCertColor = (s) => ({SQF:'#2196F3',FSSC22000:'#4CAF50',BRCGS:'#FF9800',AIB:'#9C27B0',IFS:'#F44336'}[s]||'#9E9E9E');
  const getStatusBadge = (s) => <span style={{backgroundColor:{scheduled:'#2196F3',in_progress:'#FF9800',completed:'#4CAF50',cancelled:'#F44336',pending_review:'#9C27B0'}[s]||'#9E9E9E',color:'#FFF',padding:'4px 12px',borderRadius:'12px',fontSize:'12px',fontWeight:'bold',textTransform:'uppercase'}}>{(s||'').replace('_',' ')}</span>;
  const getTypeBadge = (t) => <span style={{backgroundColor:{routine:'#2196F3',corrective:'#FF9800',emergency:'#F44336',inspection:'#9C27B0'}[t]||'#9E9E9E',color:'#FFF',padding:'4px 12px',borderRadius:'12px',fontSize:'12px',fontWeight:'bold',textTransform:'uppercase'}}>{t}</span>;
  const getPriorityBadge = (p) => <span style={{backgroundColor:{low:'#4CAF50',normal:'#2196F3',high:'#FF9800',urgent:'#F44336'}[p]||'#9E9E9E',color:'#FFF',padding:'4px 12px',borderRadius:'12px',fontSize:'12px',fontWeight:'bold',textTransform:'uppercase'}}>{p}</span>;

  const filteredClients = searchTerm.length >= 2 ? allClients.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) || (c.billing_city||'').toLowerCase().includes(searchTerm.toLowerCase()) || (c.phone||'').includes(searchTerm)) : allClients;

  const S = { // shared styles
    card: { backgroundColor:'#FFF', borderRadius:'12px', padding:'24px', boxShadow:'0 2px 8px rgba(0,0,0,0.08)', marginBottom:'20px' },
    btn: (bg,color) => ({ backgroundColor:bg, color, border:'none', padding:'10px 20px', borderRadius:'8px', cursor:'pointer', fontWeight:'600', fontSize:'14px' }),
    btnOutline: { backgroundColor:'#FFF', color:'#2D5C3F', border:'2px solid #2D5C3F', padding:'8px 16px', borderRadius:'8px', cursor:'pointer', fontWeight:'600', fontSize:'13px' },
    label: { fontSize:'12px', fontWeight:'600', color:'#666', marginBottom:'4px', display:'block' },
    input: { width:'100%', padding:'10px 12px', border:'1px solid #DDD', borderRadius:'6px', fontSize:'14px', boxSizing:'border-box' },
    grid2: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' },
    infoRow: { display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #F3F4F6', fontSize:'14px' },
    infoLabel: { color:'#888', fontWeight:'500' },
    infoValue: { color:'#333', fontWeight:'500' },
    sectionTitle: { color:'#2D5C3F', fontSize:'18px', fontWeight:'700', margin:'0 0 16px' },
  };

  // ============================
  // VIEW 1: CLIENT LIST
  // ============================
  if (!selectedClient) {
    return (
      <div style={{ maxWidth:'1000px', margin:'0 auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'24px' }}>
          <h2 style={{ color:'#2D5C3F', margin:0, fontSize:'28px' }}>Clients</h2>
          <button onClick={openCreateClient} style={S.btn('#D4AF37','#FFF')}>+ New Client</button>
        </div>

        <div style={{ ...S.card, padding:'16px', marginBottom:'20px' }}>
          <input type="text" placeholder="🔍  Search clients by name, city or phone..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ ...S.input, fontSize:'16px', border:'2px solid #E0E0E0' }} />
        </div>

        {loading ? <p>Loading...</p> : (
          <div style={S.card}>
            {filteredClients.length === 0 ? (
              <div style={{ textAlign:'center', padding:'40px', color:'#999' }}>No clients found</div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ borderBottom:'2px solid #E0E0E0', textAlign:'left' }}>
                    <th style={{ padding:'12px 16px', color:'#666', fontSize:'12px', fontWeight:'700' }}>CLIENT NAME</th>
                    <th style={{ padding:'12px 16px', color:'#666', fontSize:'12px', fontWeight:'700' }}>LOCATION</th>
                    <th style={{ padding:'12px 16px', color:'#666', fontSize:'12px', fontWeight:'700' }}>PHONE</th>
                    <th style={{ padding:'12px 16px', color:'#666', fontSize:'12px', fontWeight:'700' }}>INDUSTRY</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.sort((a,b) => a.name.localeCompare(b.name)).map(client => (
                    <tr key={client.id} onClick={() => { navigate(`/clients/${client.id}`); }}
                      style={{ borderBottom:'1px solid #F3F4F6', cursor:'pointer', transition:'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor='#F0F7F4'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor='transparent'}>
                      <td style={{ padding:'14px 16px', fontWeight:'600', color:'#2D5C3F' }}>{client.name}</td>
                      <td style={{ padding:'14px 16px', color:'#666' }}>{client.billing_city}{client.billing_state ? `, ${client.billing_state}` : ''}</td>
                      <td style={{ padding:'14px 16px', color:'#666' }}>{client.phone || '—'}</td>
                      <td style={{ padding:'14px 16px', color:'#666' }}>{client.industry || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* CLIENT MODAL */}
        {showClientModal && renderClientModal()}
        {/* CREATE CHOICE MODAL */}
        {showScannedConfirm && scannedProposal && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
            <div style={{ background:'white', borderRadius:'12px', width:'100%', maxWidth:'500px', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ background:'linear-gradient(135deg, #2D5C3F, #1A3A2A)', padding:'20px 24px', borderRadius:'12px 12px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ color:'white', fontSize:'16px', fontWeight:'700' }}>✅ Proposal Found</div>
                  <div style={{ color:'rgba(255,255,255,0.7)', fontSize:'12px' }}>SP-{String(scannedProposal.id).padStart(4,'0')} — {scannedProposal.client_name}</div>
                </div>
                <button onClick={() => { setShowScannedConfirm(false); setScannedProposal(null); }} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.7)', fontSize:'20px', cursor:'pointer' }}>&times;</button>
              </div>
              <div style={{ padding:'24px' }}>
                <div style={{ background:'#F0F7F4', borderRadius:'8px', padding:'16px', marginBottom:'20px' }}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', fontSize:'13px' }}>
                    <div><span style={{ color:'#888' }}>Client:</span><br/><strong>{scannedProposal.client_name}</strong></div>
                    <div><span style={{ color:'#888' }}>Service:</span><br/><strong>{scannedProposal.service_type}</strong></div>
                    <div><span style={{ color:'#888' }}>Frequency:</span><br/><strong>{scannedProposal.frequency}</strong></div>
                    <div><span style={{ color:'#888' }}>Investment:</span><br/><strong>${parseFloat(scannedProposal.price_per_service||0).toFixed(2)}/visit</strong></div>
                    <div><span style={{ color:'#888' }}>Facility:</span><br/><strong>{scannedProposal.facility_name || '—'}</strong></div>
                    <div><span style={{ color:'#888' }}>Duration:</span><br/><strong>{scannedProposal.contract_duration_months} months</strong></div>
                  </div>
                </div>
                <div style={{ marginBottom:'20px' }}>
                  <label style={{ fontSize:'13px', fontWeight:'600', color:'#333', display:'block', marginBottom:'6px' }}>📅 Signature Date</label>
                  <input type="date" value={signatureDate} onChange={e => setSignatureDate(e.target.value)}
                    style={{ width:'100%', padding:'10px 12px', border:'1px solid #DDD', borderRadius:'8px', fontSize:'14px', boxSizing:'border-box' }} />
                </div>
                <div style={{ display:'flex', gap:'10px' }}>
                  <button onClick={handleConfirmScanned} style={{ flex:1, padding:'12px', background:'#2D5C3F', color:'white', border:'none', borderRadius:'8px', fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
                    ⚙️ Configure Client
                  </button>
                  <button onClick={() => { setShowScannedConfirm(false); setScannedProposal(null); }} style={{ padding:'12px 20px', background:'white', color:'#666', border:'1px solid #DDD', borderRadius:'8px', fontSize:'14px', cursor:'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showCreateChoice && (
          <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}
            onClick={e => { if (e.target === e.currentTarget) setShowCreateChoice(false); }}>
            <div style={{ background:'white', borderRadius:'12px', maxWidth:'550px', width:'95%', overflow:'hidden' }}>
              <div style={{ background:'linear-gradient(135deg, #2D5C3F, #1A3A2A)', padding:'20px 24px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ color:'white', fontSize:'16px', fontWeight:'700' }}>New Client</div>
                  <div style={{ color:'rgba(255,255,255,0.6)', fontSize:'12px' }}>How would you like to create this client?</div>
                </div>
                <button onClick={() => setShowCreateChoice(false)} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.7)', fontSize:'20px', cursor:'pointer' }}>&times;</button>
              </div>
              <div style={{ padding:'24px', display:'flex', flexDirection:'column', gap:'16px' }}>
                <div style={{ border:'2px dashed #C8E6C9', borderRadius:'10px', padding:'24px', textAlign:'center' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor='#2D5C3F'; e.currentTarget.style.background='#F0F7F4'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor='#C8E6C9'; e.currentTarget.style.background='white'; }}>
                  <div style={{ fontSize:'15px', fontWeight:'700', color:'#2D5C3F', marginBottom:'4px' }}>Upload Signed Proposal</div>
                  <div style={{ fontSize:'12px', color:'#888', marginBottom:'12px' }}>Upload a scanned signed proposal PDF. The system will automatically identify the proposal and pre-fill client data.</div>
                  <label style={{ display:'inline-block', padding:'10px 24px', background:'#2D5C3F', color:'white', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'600', opacity: uploadingScanned ? 0.6 : 1 }}>
                    {uploadingScanned ? 'Processing PDF...' : 'Choose PDF File'}
                    <input type="file" accept=".pdf" style={{ display:'none' }} disabled={uploadingScanned} onChange={e => handleScannedUpload(e.target.files[0])} />
                  </label>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                  <div style={{ flex:1, height:'1px', background:'#E0E0E0' }}></div>
                  <span style={{ fontSize:'12px', color:'#999', fontWeight:'600' }}>OR</span>
                  <div style={{ flex:1, height:'1px', background:'#E0E0E0' }}></div>
                </div>
                <button onClick={startFromScratch} style={{ border:'1px solid #E0E0E0', borderRadius:'10px', padding:'20px', textAlign:'center', cursor:'pointer', background:'white', width:'100%' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor='#1565C0'; e.currentTarget.style.background='#E3F2FD'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor='#E0E0E0'; e.currentTarget.style.background='white'; }}>
                  <div style={{ fontSize:'15px', fontWeight:'700', color:'#1565C0' }}>Create from Scratch</div>
                  <div style={{ fontSize:'12px', color:'#888', marginTop:'4px' }}>Manually enter all client information.</div>
                </button>
              </div>
            </div>
          </div>
        )}

      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          confirmColor={confirmDialog.confirmColor || '#DC2626'}
          icon={confirmDialog.icon || '⚠️'}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
      </div>
    );
  }

  // ============================
  // VIEW 2: CLIENT DETAIL
  // ============================
  if (selectedClient && !selectedFacility) {
    return (
      <div style={{ maxWidth:'1000px', margin:'0 auto' }}>
        <button onClick={() => { navigate('/clients'); setSelectedClient(null); setFacilities([]); setSearchTerm(''); }} style={S.btnOutline}>← Back to Clients</button>

        {/* Client Info Card */}
        <div style={{ ...S.card, marginTop:'20px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'start', marginBottom:'20px' }}>
            <div>
              <h2 style={{ color:'#2D5C3F', margin:'0 0 4px', fontSize:'24px' }}>{selectedClient.name}</h2>
              <span style={{ color:'#888', fontSize:'14px' }}>{selectedClient.industry || 'No industry set'}</span>
            </div>
            <div style={{ display:'flex', gap:'8px' }}>
              <button onClick={openEditClient} style={S.btn('#2D5C3F','#FFF')}>✏️ Edit</button>
              <button onClick={deleteClient} style={S.btn('#F44336','#FFF')}>🗑️ Delete</button>
            </div>
          </div>

          <div style={S.grid2}>
            <div>
              <h4 style={{ color:'#2D5C3F', margin:'0 0 12px', fontSize:'14px', borderBottom:'2px solid #E8F5E9', paddingBottom:'8px' }}>📋 Contact & Billing</h4>
              <div style={S.infoRow}><span style={S.infoLabel}>Contact Email</span><span style={S.infoValue}>{selectedClient.email || '—'}</span></div>
              <div style={S.infoRow}><span style={S.infoLabel}>Billing Email</span><span style={S.infoValue}>{selectedClient.billing_email || '—'}</span></div>
              {billingEmails.length > 0 && (
                <div style={S.infoRow}>
                  <span style={S.infoLabel}>Additional Billing</span>
                  <span style={S.infoValue}>
                    {billingEmails.map((be,i) => (
                      <span key={i} style={{ display:'block', fontSize:'13px' }}>
                        {be.email}{be.label ? <span style={{ color:'#9CA3AF', fontSize:'11px' }}> ({be.label})</span> : ''}
                      </span>
                    ))}
                  </span>
                </div>
              )}
              <div style={S.infoRow}><span style={S.infoLabel}>Phone</span><span style={S.infoValue}>{selectedClient.phone || '—'}</span></div>
              <div style={S.infoRow}><span style={S.infoLabel}>Billing Address</span><span style={S.infoValue}>{selectedClient.billing_address || '—'}</span></div>
              <div style={S.infoRow}><span style={S.infoLabel}>City/State/Zip</span><span style={S.infoValue}>{[selectedClient.billing_city, selectedClient.billing_state, selectedClient.billing_zip].filter(Boolean).join(', ') || '—'}</span></div>
            </div>
            <div>
              <h4 style={{ color:'#2D5C3F', margin:'0 0 12px', fontSize:'14px', borderBottom:'2px solid #E8F5E9', paddingBottom:'8px' }}>📄 Contract</h4>
              <div style={S.infoRow}><span style={S.infoLabel}>Start Date</span><span style={S.infoValue}>{selectedClient.contract_start_date ? new Date(selectedClient.contract_start_date).toLocaleDateString() : '—'}</span></div>
              <div style={S.infoRow}><span style={S.infoLabel}>End Date</span><span style={S.infoValue}>{selectedClient.contract_end_date ? new Date(selectedClient.contract_end_date).toLocaleDateString() : '—'}</span></div>
              <div style={S.infoRow}><span style={S.infoLabel}>Status</span><span style={{ ...S.infoValue, color: selectedClient.is_active ? '#4CAF50' : '#F44336' }}>{selectedClient.is_active ? '✅ Active' : '❌ Inactive'}</span></div>
            </div>
          </div>
        </div>

        {/* Facilities Section */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', margin:'28px 0 16px' }}>
          <h3 style={S.sectionTitle}>🏭 Facilities ({facilities.length})</h3>
          <button onClick={openCreateFacility} style={S.btn('#2D5C3F','#FFF')}>+ Add Facility</button>
        </div>

        {facilities.length === 0 ? (
          <div style={{ ...S.card, textAlign:'center', padding:'60px', color:'#999' }}>
            <div style={{ fontSize:'48px', marginBottom:'12px' }}>🏭</div>
            No facilities yet. Click "Add Facility" to create one.
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(350px, 1fr))', gap:'16px' }}>
            {facilities.map(f => (
              <div key={f.id} onClick={() => { loadFacility(f); navigate(`/clients/${selectedClient.id}/facility/${f.id}`); }}
                style={{ ...S.card, cursor:'pointer', border:'2px solid transparent', transition:'all 0.2s', marginBottom:0 }}
                onMouseEnter={e => { e.currentTarget.style.borderColor='#2D5C3F'; e.currentTarget.style.transform='translateY(-3px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor='transparent'; e.currentTarget.style.transform='none'; }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'start', marginBottom:'12px' }}>
                  <h4 style={{ color:'#2D5C3F', margin:0, fontSize:'16px' }}>{f.name}</h4>
                  {f.certification_scheme && <span style={{ backgroundColor:getCertColor(f.certification_scheme), color:'#FFF', padding:'4px 12px', borderRadius:'12px', fontSize:'11px', fontWeight:'600' }}>{f.certification_scheme}</span>}
                </div>
                <div style={{ fontSize:'13px', color:'#666' }}>
                  <div>📍 {f.address}, {f.city}, {f.state} {f.zip_code}</div>
                  {f.facility_type && <div style={{ marginTop:'4px' }}>🏭 {f.facility_type}</div>}
                  {f.site_contact_name && <div style={{ marginTop:'4px' }}>👤 {f.site_contact_name}</div>}
                </div>
                <div style={{ marginTop:'12px', paddingTop:'12px', borderTop:'1px solid #E8F5E9', fontSize:'12px', color:'#2D5C3F', fontWeight:'600' }}>
                  View Details →
                </div>
              </div>
            ))}
          </div>
        )}

        {showClientModal && renderClientModal()}
        {showFacilityModal && renderFacilityModal()}
      {showActivateModal && activateProposal && (
        <ActivateServicesModal
          proposal={activateProposal}
          onSuccess={(result) => {
            setShowActivateModal(false);
            setActivateProposal(null);
            toast.success(result.message || 'Services activated successfully!');
          }}
          onSkip={() => {
            setShowActivateModal(false);
            setActivateProposal(null);
          }}
        />
      )}

      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          confirmColor={confirmDialog.confirmColor || '#DC2626'}
          icon={confirmDialog.icon || '⚠️'}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
      </div>
    );
  }

  // ============================
  // VIEW 3: FACILITY DETAIL
  // ============================
  return (
    <div style={{ maxWidth:'1000px', margin:'0 auto' }}>
      <div style={{ display:'flex', gap:'10px', marginBottom:'20px' }}>
        <button onClick={() => { navigate('/clients'); setSelectedClient(null); setSelectedFacility(null); setSearchTerm(''); }} style={{ ...S.btnOutline, color:'#888', borderColor:'#DDD' }}>← Clients</button>
        <button onClick={() => { navigate(`/clients/${selectedClient.id}`); setSelectedFacility(null); }} style={S.btnOutline}>← {selectedClient?.name}</button>
      </div>

      {/* Facility Info */}
      <div style={S.card}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'start', marginBottom:'20px' }}>
          <div>
            <h2 style={{ color:'#2D5C3F', margin:'0 0 4px', fontSize:'22px' }}>{selectedFacility?.name}</h2>
            <span style={{ color:'#888', fontSize:'14px' }}>{selectedClient?.name}</span>
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            {selectedFacility?.certification_scheme && <span style={{ backgroundColor:getCertColor(selectedFacility.certification_scheme), color:'#FFF', padding:'6px 16px', borderRadius:'20px', fontSize:'13px', fontWeight:'600' }}>{selectedFacility.certification_scheme}</span>}
            <button onClick={openEditFacility} style={S.btn('#2D5C3F','#FFF')}>✏️ Edit</button>
            <button onClick={deleteFacility} style={S.btn('#F44336','#FFF')}>🗑️</button>
          </div>
        </div>

        <div style={S.grid2}>
          <div>
            <h4 style={{ color:'#2D5C3F', margin:'0 0 12px', fontSize:'14px', borderBottom:'2px solid #E8F5E9', paddingBottom:'8px' }}>📍 Location</h4>
            <div style={S.infoRow}><span style={S.infoLabel}>Address</span><span style={S.infoValue}>{selectedFacility?.address}</span></div>
            <div style={S.infoRow}><span style={S.infoLabel}>City/State/Zip</span><span style={S.infoValue}>{[selectedFacility?.city, selectedFacility?.state, selectedFacility?.zip_code].filter(Boolean).join(', ')}</span></div>
            <div style={S.infoRow}><span style={S.infoLabel}>Type</span><span style={S.infoValue}>{selectedFacility?.facility_type || '—'}</span></div>
            <div style={S.infoRow}><span style={S.infoLabel}>Size</span><span style={S.infoValue}>{selectedFacility?.square_footage ? `${selectedFacility.square_footage.toLocaleString()} sq ft` : '—'}</span></div>
          </div>
          <div>
            <h4 style={{ color:'#2D5C3F', margin:'0 0 12px', fontSize:'14px', borderBottom:'2px solid #E8F5E9', paddingBottom:'8px' }}>👤 Site Contact & Audit</h4>
            <div style={S.infoRow}><span style={S.infoLabel}>Contact</span><span style={S.infoValue}>{selectedFacility?.site_contact_name || '—'}</span></div>
            <div style={S.infoRow}><span style={S.infoLabel}>Phone</span><span style={S.infoValue}>{selectedFacility?.site_contact_phone || '—'}</span></div>
            <div style={S.infoRow}><span style={S.infoLabel}>Email</span><span style={S.infoValue}>{selectedFacility?.site_contact_email || '—'}</span></div>
            <div style={S.infoRow}><span style={S.infoLabel}>Cert Level</span><span style={S.infoValue}>{selectedFacility?.certification_level || '—'}</span></div>
            <div style={S.infoRow}><span style={S.infoLabel}>Next Audit</span><span style={S.infoValue}>{selectedFacility?.next_audit_date ? new Date(selectedFacility.next_audit_date).toLocaleDateString() : '—'}</span></div>
          </div>
        </div>
      </div>

      {/* Phase 2: Recurring Services Section */}
      <div style={{ ...S.card }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
          <h3 style={{ color:'#2D5C3F', margin:0, fontSize:'16px' }}>🔁 Recurrent Service Pattern ({recurringServices.length})</h3>
        </div>
        {recurringServices.length === 0 ? (
          <p style={{ color:'#999', textAlign:'center', padding:'20px' }}>No recurring services set up for this facility</p>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
              <thead>
                <tr style={{ borderBottom:'2px solid #E5E7EB', textAlign:'left' }}>
                  <th style={{padding:'8px'}}>Service Type</th>
                  <th style={{padding:'8px'}}>Pattern</th>
                  <th style={{padding:'8px'}}>Tech</th>
                  <th style={{padding:'8px'}}>Last Service</th>
                  <th style={{padding:'8px'}}>Next Service</th>
                  <th style={{padding:'8px'}}>Status</th>
                  <th style={{padding:'8px'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recurringServices.map(rs => {
                  const stats = getRecurringStats(rs);
                  return (
                    <tr key={rs.id} style={{ borderBottom:'1px solid #F3F4F6' }}>
                      <td style={{padding:'8px'}}>
                        {getTypeBadge(rs.service_type)}
                        {rs.is_upgrade && <span style={{marginLeft:6, fontSize:11, color:'#92400E', background:'#FEF3C7', padding:'2px 6px', borderRadius:4}}>Upgrade</span>}
                      </td>
                      <td style={{padding:'8px'}}>
                        <div>{formatPattern(rs.recurrence_pattern, rs.recurrence_config)}</div>
                        {rs.preferred_time_start && <div style={{ color:'#888', fontSize:11, marginTop:2}}>at {fmtTime(rs.preferred_time_start)}</div>}
                      </td>
                      <td style={{padding:'8px'}}>
                        {rs.tech_first_name || rs.tech_last_name
                          ? `${rs.tech_first_name || ''} ${rs.tech_last_name || ''}`.trim()
                          : <span style={{color:'#999'}}>Unassigned</span>}
                      </td>
                      <td style={{padding:'8px'}}>{stats.lastService ? fmtDateDay(stats.lastService) : <span style={{color:'#999'}}>—</span>}</td>
                      <td style={{padding:'8px'}}>
                        {stats.nextService ? (
                          <React.Fragment>
                            <div>{fmtDateDay(stats.nextService)}</div>
                            {stats.isComputed && <div style={{ fontSize:10, color:'#92400E', fontStyle:'italic', marginTop:2}}>(estimated)</div>}
                          </React.Fragment>
                        ) : <span style={{color:'#999'}}>—</span>}
                      </td>
                      <td style={{padding:'8px'}}>
                        {rs.is_active
                          ? <span style={{padding:'2px 8px', background:'#D1FAE5', color:'#065F46', borderRadius:4, fontSize:11, fontWeight:600}}>Active</span>
                          : <span style={{padding:'2px 8px', background:'#FEE2E2', color:'#991B1B', borderRadius:4, fontSize:11, fontWeight:600}}>Stopped</span>}
                      </td>
                      <td style={{padding:'8px'}}>
                        {rs.is_active && (
                          <React.Fragment>
                            <button
                              onClick={() => { setEditPatternTarget(rs); setShowEditPatternModal(true); }}
                              style={{padding:'4px 10px', fontSize:12, background:'#DBEAFE', color:'#1E40AF', border:'1px solid #93C5FD', borderRadius:4, cursor:'pointer', marginRight:6}}
                            >Edit</button>
                            <button
                              onClick={() => { setStopRecurringTarget(rs); setShowStopRecurringModal(true); }}
                              style={{padding:'4px 10px', fontSize:12, background:'#FEE2E2', color:'#991B1B', border:'1px solid #FCA5A5', borderRadius:4, cursor:'pointer'}}
                            >Stop</button>
                          </React.Fragment>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Approved Products Section */}
      <div style={{ ...S.card }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
          <h3 style={{ color:'#2D5C3F', margin:0, fontSize:'16px' }}>📦 Approved Products ({facilityProducts.length})</h3>
          <button onClick={openProductModal} style={S.btn('#2D5C3F','#FFF')}>Manage Products</button>
        </div>
        {facilityProducts.length === 0 ? (
          <p style={{ color:'#999', textAlign:'center', padding:'20px' }}>No products approved yet</p>
        ) : (
          <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
            {facilityProducts.map(p => (
              <span key={p.product_id||p.id} style={{ padding:'4px 12px', backgroundColor:'#E8F5E9', color:'#2D5C3F', borderRadius:'16px', fontSize:'12px', fontWeight:'500' }}>
                {p.name} {p.epa_number && <span style={{color:'#888'}}>({p.epa_number})</span>}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Report Recipients Section */}
      <div style={{ ...S.card }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
          <h3 style={{ color:'#2D5C3F', margin:0, fontSize:'16px' }}>✉️ Report Recipients ({recipients.length + (selectedFacility?.site_contact_email ? 1 : 0)})</h3>
          {!showRecipientForm && (
            <button onClick={() => { setShowRecipientForm(true); setRecipientEdit(null); setRecipientForm({ email:'', contact_name:'' }); }}
              style={S.btn('#2D5C3F','#FFF')}>+ Add Recipient</button>
          )}
        </div>

        {/* Add/Edit Form */}
        {showRecipientForm && (
          <div style={{ background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:'8px', padding:'16px', marginBottom:'16px' }}>
            <p style={{ margin:'0 0 12px', fontWeight:'600', fontSize:'14px', color:'#111827' }}>{recipientEdit ? 'Edit Recipient' : 'New Recipient'}</p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'12px' }}>
              <div>
                <label style={S.label}>Email *</label>
                <input type="email" style={S.input} placeholder="email@example.com"
                  value={recipientForm.email} onChange={e => setRecipientForm(p => ({ ...p, email: e.target.value }))} />
              </div>
              <div>
                <label style={S.label}>Contact Name</label>
                <input type="text" style={S.input} placeholder="Optional"
                  value={recipientForm.contact_name} onChange={e => setRecipientForm(p => ({ ...p, contact_name: e.target.value }))} />
              </div>
            </div>
            <div style={{ display:'flex', gap:'8px' }}>
              <button onClick={saveRecipient} disabled={recipientSaving}
                style={S.btn(recipientSaving ? '#9CA3AF' : '#2D5C3F', '#FFF')}>
                {recipientSaving ? 'Saving...' : (recipientEdit ? 'Update' : 'Add Recipient')}
              </button>
              <button onClick={() => { setShowRecipientForm(false); setRecipientEdit(null); }}
                style={{ ...S.btnOutline }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Site Contact — always first, not editable */}
        {selectedFacility?.site_contact_email && (
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', background:'#F0F7F4', borderRadius:'8px', border:'1px solid #2D5C3F22', marginBottom:'8px' }}>
            <div>
              <span style={{ fontSize:'14px', fontWeight:'600', color:'#1A3A2A' }}>{selectedFacility.site_contact_email}</span>
              <span style={{ marginLeft:'10px', fontSize:'11px', color:'#6B7280', background:'#E5E7EB', padding:'2px 8px', borderRadius:'10px' }}>Site Contact · Default</span>
            </div>
            <span style={{ fontSize:'16px' }}>🔒</span>
          </div>
        )}

        {/* Additional Recipients */}
        {recipients.length === 0 && !showRecipientForm ? (
          <p style={{ color:'#999', textAlign:'center', padding:'16px' }}>No additional recipients configured</p>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
            {recipients.map(rec => (
              <div key={rec.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', background:'#FFF', border:'1px solid #E5E7EB', borderRadius:'8px' }}>
                <div>
                  <span style={{ fontSize:'14px', fontWeight:'600', color:'#111827' }}>{rec.email}</span>
                  {rec.contact_name && <span style={{ marginLeft:'10px', fontSize:'12px', color:'#6B7280' }}>{rec.contact_name}</span>}
                </div>
                <div style={{ display:'flex', gap:'6px' }}>
                  <button onClick={() => { setRecipientEdit(rec); setRecipientForm({ email: rec.email, contact_name: rec.contact_name || '' }); setShowRecipientForm(true); }}
                    style={{ padding:'5px 12px', background:'#F3F4F6', border:'none', borderRadius:'6px', fontSize:'12px', fontWeight:'600', color:'#374151', cursor:'pointer' }}>Edit</button>
                  <button onClick={() => deleteRecipient(rec)}
                    style={{ padding:'5px 12px', background:'#FEF2F2', border:'none', borderRadius:'6px', fontSize:'12px', fontWeight:'600', color:'#DC2626', cursor:'pointer' }}>Remove</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* SERVICE HISTORY SECTION */}
      <div style={{ ...S.card, padding:0, overflow:'hidden' }}>
        {/* Header — collapsible */}
        <div onClick={() => setShowHistory(h => !h)}
          style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 20px',
            background: showHistory ? '#2D5C3F' : '#F9FAFB', cursor:'pointer',
            borderBottom: showHistory ? '2px solid #1A3A2A' : 'none', transition:'background 0.2s' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
            <h3 style={{ margin:0, fontSize:'16px', fontWeight:'700', color: showHistory ? '#FFF' : '#2D5C3F' }}>
              📋 Service History
            </h3>
            <span style={{ fontSize:'12px', fontWeight:'600', padding:'3px 10px', borderRadius:'12px',
              background: showHistory ? 'rgba(255,255,255,0.2)' : '#E8F5E9', color: showHistory ? '#FFF' : '#2D5C3F' }}>
              {completedOrders.length} service{completedOrders.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <span style={{ fontSize:'20px', color: showHistory ? '#D4AF37' : '#9CA3AF', fontWeight:'bold' }}>
              {showHistory ? '▲' : '▼'}
            </span>
          </div>
        </div>

        {showHistory && (
          <div style={{ padding:'16px 20px' }}>
            {/* Date filters */}
            <div style={{ display:'flex', gap:'12px', alignItems:'center', marginBottom:'16px', flexWrap:'wrap' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <label style={{ fontSize:'12px', fontWeight:'600', color:'#6B7280' }}>From:</label>
                <input type="date" value={historyDateFrom} onChange={e => setHistoryDateFrom(e.target.value)}
                  style={{ padding:'6px 10px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'13px' }} />
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <label style={{ fontSize:'12px', fontWeight:'600', color:'#6B7280' }}>To:</label>
                <input type="date" value={historyDateTo} onChange={e => setHistoryDateTo(e.target.value)}
                  style={{ padding:'6px 10px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'13px' }} />
              </div>
              {(historyDateFrom || historyDateTo) && (
                <button onClick={() => { setHistoryDateFrom(''); setHistoryDateTo(''); }}
                  style={{ padding:'6px 12px', background:'#F3F4F6', border:'none', borderRadius:'6px', fontSize:'12px', color:'#6B7280', cursor:'pointer', fontWeight:'600' }}>
                  Clear
                </button>
              )}
            </div>

            {completedOrders.length === 0 ? (
              <div style={{ textAlign:'center', padding:'32px', color:'#9CA3AF' }}>No completed services found</div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ borderBottom:'2px solid #E5E7EB', textAlign:'left', background:'#F9FAFB' }}>
                    <th style={{ padding:'10px 12px', color:'#6B7280', fontSize:'11px', fontWeight:'700', textTransform:'uppercase' }}>WO #</th>
                    <th style={{ padding:'10px 12px', color:'#6B7280', fontSize:'11px', fontWeight:'700', textTransform:'uppercase' }}>Original</th>
                    <th style={{ padding:'10px 12px', color:'#6B7280', fontSize:'11px', fontWeight:'700', textTransform:'uppercase' }}>Scheduled</th>
                    <th style={{ padding:'10px 12px', color:'#6B7280', fontSize:'11px', fontWeight:'700', textTransform:'uppercase' }}>Duration</th>
                    <th style={{ padding:'10px 12px', color:'#6B7280', fontSize:'11px', fontWeight:'700', textTransform:'uppercase' }}>Technician</th>
                    <th style={{ padding:'10px 12px', color:'#6B7280', fontSize:'11px', fontWeight:'700', textTransform:'uppercase' }}>Type</th>
                    <th style={{ padding:'10px 12px', color:'#6B7280', fontSize:'11px', fontWeight:'700', textTransform:'uppercase' }}>Report</th>
                    <th style={{ padding:'10px 12px', color:'#6B7280', fontSize:'11px', fontWeight:'700', textTransform:'uppercase' }}></th>
                  </tr>
                </thead>
                {(() => {
                  // Sub-session D Step 14b-2: split completed by Contract Base / Upgrades & Punctual
                  const renderCompletedRow = (o, i) => (
                    <tr key={o.id}
                      onClick={() => (o.report_status === 'sent' || o.report_status === 'auto_released' || o.report_status === 'pending_review') && openReportPreview(o)}
                      style={{ borderBottom:'1px solid #F3F4F6', background: i % 2 === 0 ? '#FFF' : '#FAFAFA',
                        cursor: (o.report_status === 'sent' || o.report_status === 'auto_released' || o.report_status === 'pending_review') ? 'pointer' : 'default' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor='#F0F7F4'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor= i % 2 === 0 ? '#FFF' : '#FAFAFA'}>
                      <td style={{ padding:'11px 12px', fontWeight:'700', color:'#2D5C3F', fontSize:'13px' }}>
                        {o.order_number}
                        {o.recurring_is_upgrade === true && (
                          <span style={{ marginLeft:6, fontSize:9, fontWeight:700, color:'#92400E', background:'#FEF3C7', padding:'2px 5px', borderRadius:4, verticalAlign:'middle' }}>UPG</span>
                        )}
                      </td>
                      <td style={{ padding:'11px 12px', color:'#374151', fontSize:'12px' }}>
                        <div>{fmtDateDay(o.original_scheduled_date || o.scheduled_date)}</div>
                        <div style={{ fontSize:11, color:'#9CA3AF', marginTop:2 }}>
                          {fmtTime(o.original_scheduled_time_start || o.scheduled_time_start)}
                        </div>
                      </td>
                      <td style={{ padding:'11px 12px', fontSize:'12px', ...((o.original_scheduled_date && o.original_scheduled_date !== o.scheduled_date) || (o.original_scheduled_time_start && o.original_scheduled_time_start !== o.scheduled_time_start) ? { background:'#FEF3C7', color:'#92400E', fontWeight:600 } : { color:'#374151' }) }}>
                        <div>
                          {fmtDateDay(o.scheduled_date)}
                          {o.original_scheduled_date && o.original_scheduled_date !== o.scheduled_date && (
                            <span style={{ marginLeft:6, fontSize:9, fontWeight:700, color:'#92400E', background:'#FFF', border:'1px solid #F59E0B', padding:'1px 5px', borderRadius:4, verticalAlign:'middle' }}>⟳ RESCHED</span>
                          )}
                        </div>
                        <div style={{ fontSize:11, marginTop:2, color: (o.original_scheduled_time_start && o.original_scheduled_time_start !== o.scheduled_time_start) ? '#92400E' : '#9CA3AF', fontWeight: (o.original_scheduled_time_start && o.original_scheduled_time_start !== o.scheduled_time_start) ? 700 : 400 }}>
                          {fmtTime(o.scheduled_time_start)}
                          {o.original_scheduled_time_start && o.original_scheduled_time_start !== o.scheduled_time_start && (
                            <span style={{ marginLeft:6, fontSize:9, fontWeight:700, color:'#92400E', background:'#FFF', border:'1px solid #F59E0B', padding:'1px 5px', borderRadius:4, verticalAlign:'middle' }}>⏰</span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding:'11px 12px', color:'#374151', fontSize:'13px', fontWeight:'600' }}>{calcDuration(o.service_start_time, o.service_end_time)}</td>
                      <td style={{ padding:'11px 12px', color:'#374151', fontSize:'13px' }}>{o.tech_first_name ? `${o.tech_first_name} ${o.tech_last_name}` : '—'}</td>
                      <td style={{ padding:'11px 12px' }}>{getTypeBadge(o.service_type)}</td>
                      <td style={{ padding:'11px 12px' }}>
                        {o.report_status === 'sent' || o.report_status === 'auto_released' ? (
                          <span style={{ fontSize:'12px', fontWeight:'600', color:'#059669', background:'#D1FAE5', padding:'3px 10px', borderRadius:'10px' }}>✓ Sent</span>
                        ) : o.report_status === 'pending_review' ? (
                          <span style={{ fontSize:'12px', fontWeight:'600', color:'#D97706', background:'#FEF3C7', padding:'3px 10px', borderRadius:'10px' }}>⏳ Review</span>
                        ) : (
                          <span style={{ fontSize:'12px', color:'#9CA3AF' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding:'11px 12px', textAlign:'right' }}>
                        {(o.report_status === 'sent' || o.report_status === 'auto_released' || o.report_status === 'pending_review') && (
                          <button onClick={() => openReportPreview(o)}
                            style={{ padding:'5px 14px', background:'#2D5C3F', color:'white', border:'none', borderRadius:'6px', fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>
                            View Report
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                  const subHeader = (label, n, bg) => (
                    <tr><td colSpan={8} style={{ padding:'10px 12px', background: bg, fontWeight:700, fontSize:12, color:'#374151', textTransform:'uppercase', letterSpacing:0.5, borderBottom:'1px solid #E5E7EB' }}>{label} ({n})</td></tr>
                  );
                  // Sub-session D Step 22: always split when at least one upgrade exists
                  if (completedExtra.length > 0) {
                    return (
                      <>
                        <tbody>
                          {subHeader('Contract Base', completedBase.length, '#ECFDF5')}
                          {completedBase.length > 0 ? completedBase.map(renderCompletedRow) : (
                            <tr><td colSpan={8} style={{ padding:'14px 12px', fontSize:12, color:'#6B7280', fontStyle:'italic', background:'#FFF', borderBottom:'1px solid #F3F4F6' }}>No contract base completed history for this facility.</td></tr>
                          )}
                        </tbody>
                        <tbody>
                          {subHeader('Upgrades & Punctual', completedExtra.length, '#FEF3C7')}
                          {completedExtra.map(renderCompletedRow)}
                        </tbody>
                      </>
                    );
                  }
                  return <tbody>{completedOrders.map(renderCompletedRow)}</tbody>;
                })()}
              </table>
            )}
          </div>
        )}
      </div>

      {/* SCHEDULED ORDERS SECTION */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', margin:'20px 0 16px' }}>
        <h3 style={S.sectionTitle}>📅 Scheduled Orders ({pendingOrders.length})</h3>
        <button onClick={() => { setShowSOModal(true); setSelectedOrder(null); }} style={S.btn('#D4AF37','#FFF')}>+ Create Service Order</button>
      </div>

      {pendingOrders.length === 0 ? (
        <div style={{ ...S.card, textAlign:'center', padding:'40px', color:'#999' }}>No scheduled orders</div>
      ) : (
        <div style={S.card}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ borderBottom:'2px solid #E0E0E0', textAlign:'left' }}>
                <th style={{ padding:'12px', color:'#666', fontSize:'12px' }}>ORDER #</th>
                <th style={{ padding:'12px', color:'#666', fontSize:'12px' }}>TYPE</th>
                <th style={{ padding:'12px', color:'#666', fontSize:'12px' }}>ORIGINAL</th>
                <th style={{ padding:'12px', color:'#666', fontSize:'12px' }}>SCHEDULED</th>
                <th style={{ padding:'12px', color:'#666', fontSize:'12px' }}>TECHNICIAN</th>
                <th style={{ padding:'12px', color:'#666', fontSize:'12px' }}>PRIORITY</th>
                <th style={{ padding:'12px', color:'#666', fontSize:'12px' }}>STATUS</th>
              </tr>
            </thead>
            {(() => {
              // Sub-session D Step 14b-2: split into Contract Base + Upgrades & Punctual
              const renderRow = (o) => (
                <tr key={o.id} onClick={() => { setSelectedOrder(o); setShowSOModal(true); }}
                  style={{ borderBottom:'1px solid #F3F4F6', cursor:'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor='#F0F7F4'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor='transparent'}>
                  <td style={{ padding:'12px', fontWeight:'600', color:'#2D5C3F' }}>
                    {o.order_number}
                    {o.recurring_is_upgrade === true && (
                      <span style={{ marginLeft:6, fontSize:9, fontWeight:700, color:'#92400E', background:'#FEF3C7', padding:'2px 5px', borderRadius:4, verticalAlign:'middle' }}>UPG</span>
                    )}
                  </td>
                  <td style={{ padding:'12px' }}>{getTypeBadge(o.service_type)}</td>
                  <td style={{ padding:'12px', fontSize:12 }}>
                    <div>{fmtDateDay(o.original_scheduled_date || o.scheduled_date)}</div>
                    <div style={{ fontSize:11, color:'#6B7280', marginTop:2 }}>
                      {fmtTime(o.original_scheduled_time_start || o.scheduled_time_start)}
                    </div>
                  </td>
                  <td style={{ padding:'12px', fontSize:12, ...((o.original_scheduled_date && o.original_scheduled_date !== o.scheduled_date) || (o.original_scheduled_time_start && o.original_scheduled_time_start !== o.scheduled_time_start) ? { background:'#FEF3C7', color:'#92400E', fontWeight:600 } : {}) }}>
                    <div>
                      {fmtDateDay(o.scheduled_date)}
                      {o.original_scheduled_date && o.original_scheduled_date !== o.scheduled_date && (
                        <span style={{ marginLeft:6, fontSize:9, fontWeight:700, color:'#92400E', background:'#FFF', border:'1px solid #F59E0B', padding:'1px 5px', borderRadius:4, verticalAlign:'middle' }}>⟳ RESCHED</span>
                      )}
                    </div>
                    <div style={{ fontSize:11, marginTop:2, color: (o.original_scheduled_time_start && o.original_scheduled_time_start !== o.scheduled_time_start) ? '#92400E' : '#6B7280', fontWeight: (o.original_scheduled_time_start && o.original_scheduled_time_start !== o.scheduled_time_start) ? 700 : 400 }}>
                      {fmtTime(o.scheduled_time_start)}
                      {o.original_scheduled_time_start && o.original_scheduled_time_start !== o.scheduled_time_start && (
                        <span style={{ marginLeft:6, fontSize:9, fontWeight:700, color:'#92400E', background:'#FFF', border:'1px solid #F59E0B', padding:'1px 5px', borderRadius:4, verticalAlign:'middle' }}>⏰</span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding:'12px' }}>{o.tech_first_name ? `${o.tech_first_name} ${o.tech_last_name}` : 'Unassigned'}</td>
                  <td style={{ padding:'12px' }}>{getPriorityBadge(o.priority)}</td>
                  <td style={{ padding:'12px' }}>{getStatusBadge(o.status)}</td>
                </tr>
              );
              const subHeader = (label, n, bg) => (
                <tr><td colSpan={7} style={{ padding:'10px 12px', background: bg, fontWeight:700, fontSize:12, color:'#374151', textTransform:'uppercase', letterSpacing:0.5, borderBottom:'1px solid #E5E7EB' }}>{label} ({n})</td></tr>
              );
              // Sub-session D Step 22: always split when at least one upgrade exists,
              // even if base is empty (clearer for admin to spot orphan upgrade setups).
              if (pendingExtra.length > 0) {
                return (
                  <>
                    <tbody>
                      {subHeader('Contract Base', pendingBase.length, '#ECFDF5')}
                      {pendingBase.length > 0 ? pendingBase.map(renderRow) : (
                        <tr><td colSpan={7} style={{ padding:'14px 12px', fontSize:12, color:'#6B7280', fontStyle:'italic', background:'#FFF', borderBottom:'1px solid #F3F4F6' }}>No contract base for this facility — only upgrades or punctual services exist.</td></tr>
                      )}
                    </tbody>
                    <tbody>
                      {subHeader('Upgrades & Punctual', pendingExtra.length, '#FEF3C7')}
                      {pendingExtra.map(renderRow)}
                    </tbody>
                  </>
                );
              }
              return <tbody>{pendingOrders.map(renderRow)}</tbody>;
            })()}
          </table>
        </div>
      )}

      {/* DEVICES & STRUCTURE SECTION */}
      <div style={{ ...S.card, padding:0, overflow:'hidden', marginBottom:16 }}>
        <div onClick={() => { setShowDevices(d => !d); if (!showDevices && selectedFacility) fetchDeviceTree(selectedFacility.id); }}
          style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 20px',
            background: showDevices ? '#1565C0' : '#F9FAFB', cursor:'pointer',
            borderBottom: showDevices ? '2px solid #0D47A1' : 'none' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <h3 style={{ margin:0, fontSize:16, fontWeight:700, color: showDevices ? '#FFF' : '#1565C0' }}>
              🗺️ Devices & Structure
            </h3>
            <span style={{ fontSize:12, fontWeight:600, padding:'3px 10px', borderRadius:12,
              background: showDevices ? 'rgba(255,255,255,0.2)' : '#E3F2FD', color: showDevices ? '#FFF' : '#1565C0' }}>
              {deviceTree.length > 0 ? deviceTree.length + ' top-level node(s)' : 'Click to load'}
            </span>
          </div>
          <span style={{ fontSize:20, color: showDevices ? '#D4AF37' : '#9CA3AF', fontWeight:'bold' }}>
            {showDevices ? '▲' : '▼'}
          </span>
        </div>
        {showDevices && (
          <div style={{ padding:'16px 20px' }}>
            {devicesLoading ? (
              <div style={{ textAlign:'center', padding:32, color:'#999' }}>Loading facility structure...</div>
            ) : deviceTree.length === 0 ? (
              <div style={{ textAlign:'center', padding:32, color:'#999' }}>No devices or structure nodes found for this facility</div>
            ) : (
              <DeviceTreeView nodes={deviceTree} level={0} facilityId={selectedFacility?.id} onRefresh={()=>fetchDeviceTree(selectedFacility.id)} deviceTypes={deviceTypesList} onConfirmDelete={(node,cb)=>setNodeConfirm({node,cb})} onError={(msg)=>toast.error(msg)} onMoveNode={(node)=>setMoveNodeData({node,targetId:""})} />
            )}
          </div>
        )}
      </div>

      {/* ── Complaint Logs ─────────────────────────────────────────────── */}
      <div style={{ ...S.card, padding:0, overflow:'hidden', marginBottom:16 }}>
        <div onClick={() => { setShowComplaintLogs(d => !d); if (!showComplaintLogs && selectedFacility) fetchComplaintLogs(selectedFacility.id); }}
          style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 20px',
            background: showComplaintLogs ? '#2D5C3F' : '#F9FAFB', cursor:'pointer',
            borderBottom: showComplaintLogs ? '2px solid #1A3A2A' : 'none' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <h3 style={{ margin:0, fontSize:16, fontWeight:700, color: showComplaintLogs ? '#FFF' : '#2D5C3F' }}>
              📋 Complaint Logs
            </h3>
            <span style={{ fontSize:12, fontWeight:600, padding:'3px 10px', borderRadius:12,
              background: showComplaintLogs ? 'rgba(255,255,255,0.2)' : '#E8F5E9', color: showComplaintLogs ? '#FFF' : '#2D5C3F' }}>
              {complaintLogs.length > 0 ? complaintLogs.length + ' log(s)' : 'Click to load'}
            </span>
          </div>
          <span style={{ fontSize:20, color: showComplaintLogs ? '#D4AF37' : '#9CA3AF', fontWeight:'bold' }}>
            {showComplaintLogs ? '▲' : '▼'}
          </span>
        </div>
        {showComplaintLogs && (
          <div style={{ padding:'16px 20px' }}>
            {complaintLogsLoading ? (
              <div style={{ textAlign:'center', padding:32, color:'#999' }}>Loading complaint logs...</div>
            ) : (
              <>
                <div style={{ marginBottom:16 }}>
                  {complaintLogs.length === 0 && (
                    <div style={{ textAlign:'center', padding:'24px', color:'#999', fontSize:14 }}>
                      No complaint logs configured for this facility
                    </div>
                  )}
                  {complaintLogs.map(log => (
                    <div key={log.id} style={{ border:'1px solid #E5E7EB', borderRadius:8, padding:'12px 16px', marginBottom:10, background:'#FAFAFA' }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <span style={{ fontWeight:700, fontSize:14, color:'#1A3A2A' }}>{log.name}</span>
                          <span style={{ fontSize:11, padding:'2px 8px', borderRadius:10, fontWeight:600,
                            background: log.log_type===1?'#E8F5E9':log.log_type===2?'#E3F2FD':'#FFF3E0',
                            color: log.log_type===1?'#2D5C3F':log.log_type===2?'#1565C0':'#E65100' }}>
                            Type {log.log_type} — {log.log_type===1?'Basic':log.log_type===2?'Two-section':'Sectional'}
                          </span>
                          {log.section_name && (
                            <span style={{ fontSize:11, padding:'2px 8px', borderRadius:10, background:'#F3F4F6', color:'#6B7280' }}>
                              {log.section_name}
                            </span>
                          )}
                        </div>
                        <div style={{ display:'flex', gap:6 }}>
                          <button onClick={() => {
                              const token = localStorage.getItem('token');
                              window.open(`https://api.safecorepest.com/api/complaint-logs/${log.id}/pdf?token=${token}`, '_blank');
                            }}
                            style={{ fontSize:11, padding:'3px 10px', borderRadius:6, border:'1px solid #2D5C3F', background:'#F0F7F4', color:'#2D5C3F', cursor:'pointer' }}>
                            View PDF
                          </button>
                          {log.status !== 'approved' ? (
                            <button onClick={() => setConfirmDialog({
                              message: `Approve "${log.name}" and archive PDF to the facility Documents?`,
                              confirmText: 'Approve & Archive',
                              icon: '✅',
                              onConfirm: async () => {
                                setConfirmDialog(null);
                                try {
                                  const token = localStorage.getItem('token');
                                  const r = await fetch(`https://api.safecorepest.com/api/complaint-logs/${log.id}/approve`,
                                    { method:'POST', headers:{ Authorization:`Bearer ${token}` } });
                                  const d = await r.json();
                                  if (d.success) {
                                    toast.success('Log approved and archived to Documents');
                                    fetchComplaintLogs(selectedFacility.id);
                                    const rd = await fetch(`https://api.safecorepest.com/api/facility-docs/${selectedFacility.id}/documents`,
                                      { headers:{ Authorization:`Bearer ${token}` } });
                                    const dd = await rd.json();
                                    setFacilityDocs(dd.data || []);
                                  } else toast.error(d.error || 'Error');
                                } catch(e) { toast.error('Error approving log'); }
                              },
                              onCancel: () => setConfirmDialog(null)
                            })} style={{ fontSize:11, padding:'3px 10px', borderRadius:6, border:'1px solid #2D5C3F', background:'#E8F5E9', color:'#2D5C3F', cursor:'pointer' }}>
                              ✓ Approve
                            </button>
                          ) : (
                            <span style={{ fontSize:11, padding:'3px 10px', borderRadius:6, background:'#D1FAE5', color:'#065F46', fontWeight:600 }}>
                              ✓ Approved
                            </span>
                          )}
                          <button onClick={() => { setEditComplaintLog(log); setClForm({ name:log.name, log_type:log.log_type, section_id:log.section_id||'', requires_review:log.requires_review }); }}
                            style={{ fontSize:11, padding:'3px 10px', borderRadius:6, border:'1px solid #D4AF37', background:'#FFF8E7', color:'#92400E', cursor:'pointer' }}>
                            Edit
                          </button>
                          <button onClick={() => deleteComplaintLog(log.id, log.name)}
                            style={{ fontSize:11, padding:'3px 10px', borderRadius:6, border:'1px solid #FCA5A5', background:'#FEF2F2', color:'#991B1B', cursor:'pointer' }}>
                            Delete
                          </button>
                        </div>
                      </div>
                      <div style={{ fontSize:12, color:'#6B7280', display:'flex', gap:16 }}>
                        <span>📊 {log.total_entries || 0} total sightings</span>
                        {log.pending_entries > 0 && <span style={{ color:'#DC2626', fontWeight:600 }}>⚠ {log.pending_entries} pending</span>}
                        {log.requires_review && <span style={{ color:'#2D5C3F' }}>✓ Blocks WO completion</span>}
                      </div>
                    </div>
                  ))}
                </div>
                {(() => {
                  const hasType1 = complaintLogs.some(l => l.log_type === 1);
                  const hasOther = complaintLogs.some(l => l.log_type !== 1);
                  const canAdd = !hasType1 && complaintLogs.length < 20;
                  const reason = hasType1 ? 'Type 1 allows only one log per facility. Delete it first to add a different type.' : null;
                  return canAdd ? (
                    <button onClick={() => setShowAddComplaintLog(true)}
                      style={{ width:'100%', padding:'10px', borderRadius:8, border:'2px dashed #2D5C3F',
                        background:'transparent', color:'#2D5C3F', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                      + Add Complaint Log {hasOther ? '(another section)' : ''}
                    </button>
                  ) : reason ? (
                    <div style={{ textAlign:'center', padding:'10px', fontSize:12, color:'#92400E',
                      background:'#FFF8E7', borderRadius:8, border:'1px solid #D4AF37' }}>
                      ⚠ {reason}
                    </div>
                  ) : null;
                })()}
              </>
            )}
          </div>
        )}
      </div>

      {(showAddComplaintLog || editComplaintLog) && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:12, padding:28, width:480, maxWidth:'92vw' }}>
            <h3 style={{ margin:'0 0 20px', fontSize:16, color:'#1A3A2A' }}>
              {editComplaintLog ? '✏️ Edit Complaint Log' : '➕ Add Complaint Log'}
            </h3>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:4 }}>Log Type *</label>
              <select value={clForm.log_type} disabled={!!editComplaintLog}
                onChange={e => {
                  const t = parseInt(e.target.value);
                  setClForm(f=>({...f, log_type:t, name: t===1 ? 'General Pest Sighting Log' : f.name==='General Pest Sighting Log' ? '' : f.name}));
                }} style={{ width:'100%', padding:'8px 12px', borderRadius:6, border:'1px solid #D1D5DB', fontSize:13,
                  background: editComplaintLog ? '#F9FAFB' : 'white', color: editComplaintLog ? '#6B7280' : '#111' }}>
                <option value={1}>Type 1 — Basic (small plant, one log for entire facility)</option>
                <option value={2}>Type 2 — Two-section (medium plant, staff + tech sections)</option>
                <option value={3}>Type 3 — Sectional (large plant, one log per area)</option>
              </select>
              {editComplaintLog && (
                <div style={{ fontSize:11, color:'#6B7280', marginTop:4 }}>Log type cannot be changed after creation. Delete and recreate if needed.</div>
              )}
            </div>
            {clForm.log_type === 1 ? (
              <div style={{ marginBottom:14, padding:'10px 12px', background:'#F0F7F4', borderRadius:6, fontSize:13, color:'#2D5C3F', fontWeight:500 }}>
                📋 Log name: <strong>General Pest Sighting Log</strong> <span style={{ fontWeight:400, color:'#6B7280' }}>(standard for single-log facilities)</span>
              </div>
            ) : (
              <>
                {!editComplaintLog && (
                  <div style={{ marginBottom:14 }}>
                    <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:4 }}>
                      How many sections does this facility have? *
                    </label>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <input type="number" min={1} max={10} value={clForm.sectionCount||1}
                        onChange={e => {
                          const n = Math.min(10, Math.max(1, parseInt(e.target.value)||1));
                          const names = Array.from({length:n}, (_,i) => (clForm.sectionNames||[])[i] || '');
                          setClForm(f=>({...f, sectionCount:n, sectionNames:names}));
                        }}
                        style={{ width:70, padding:'8px 12px', borderRadius:6, border:'1px solid #D1D5DB', fontSize:13 }} />
                      <span style={{ fontSize:12, color:'#6B7280' }}>log(s) will be created — one per section</span>
                    </div>
                  </div>
                )}
                {!editComplaintLog ? (
                  <div style={{ marginBottom:14 }}>
                    <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:8 }}>
                      Section names *
                    </label>
                    {Array.from({length: clForm.sectionCount||1}, (_,i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                        <span style={{ fontSize:12, fontWeight:600, color:'#6B7280', minWidth:20 }}>{i+1}.</span>
                        <input value={(clForm.sectionNames||[])[i]||''}
                          onChange={e => {
                            const names = [...(clForm.sectionNames||[])];
                            names[i] = e.target.value;
                            setClForm(f=>({...f, sectionNames:names}));
                          }}
                          placeholder={clForm.log_type===2 ? `e.g. Dock Area, Production Floor` : `e.g. Section ${i+1}`}
                          style={{ flex:1, padding:'7px 10px', borderRadius:6, border:'1px solid #D1D5DB', fontSize:13 }} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ marginBottom:14 }}>
                    <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:4 }}>
                      Log Name *
                    </label>
                    <input value={clForm.name} onChange={e => setClForm(f=>({...f, name:e.target.value}))}
                      style={{ width:'100%', padding:'8px 12px', borderRadius:6, border:'1px solid #D1D5DB', fontSize:13, boxSizing:'border-box' }} />
                  </div>
                )}
              </>
            )}
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:4 }}>Linked Section (optional)</label>
              <select value={clForm.section_id} onChange={e => setClForm(f=>({...f, section_id:e.target.value}))}
                style={{ width:'100%', padding:'8px 12px', borderRadius:6, border:'1px solid #D1D5DB', fontSize:13 }}>
                <option value="">— No section linked —</option>
                {facilityStructureFlat.map(s => (
                  <option key={s.id} value={s.id}>{s.node_type.toUpperCase()} — {s.name}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                <input type="checkbox" checked={clForm.requires_review}
                  onChange={e => setClForm(f=>({...f, requires_review:e.target.checked}))} />
                <span style={{ fontSize:13, color:'#374151' }}>Block WO completion if log not reviewed</span>
              </label>
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => { setShowAddComplaintLog(false); setEditComplaintLog(null); setClForm({name:'',log_type:1,section_id:'',requires_review:true}); }}
                style={{ padding:'8px 18px', borderRadius:6, border:'1px solid #D1D5DB', background:'#F9FAFB', color:'#374151', cursor:'pointer', fontSize:13 }}>
                Cancel
              </button>
              <button onClick={saveComplaintLog}
                style={{ padding:'8px 20px', borderRadius:6, border:'none', background:'#2D5C3F', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:600 }}>
                {editComplaintLog ? 'Save Changes' : clForm.log_type === 1 ? 'Add Log' : `Create ${clForm.sectionCount||1} Log(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {moveNodeData && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#fff',borderRadius:12,padding:28,width:460,maxWidth:'92vw',maxHeight:'85vh',display:'flex',flexDirection:'column'}}>
            <h3 style={{margin:'0 0 4px',fontSize:16,color:'#1a1a1a'}}>↕️ Move "{moveNodeData.node.name}"</h3>
            <p style={{fontSize:13,color:'#666',marginBottom:12}}>Click on the destination node:</p>
            {moveNodeData.targetId && (
              <div style={{marginBottom:10,padding:'6px 12px',background:'#E8F5E9',borderRadius:8,fontSize:13,color:'#2D5C3F',fontWeight:600}}>
                ✓ Selected destination: {(function findName(nodes){
                  for(const n of nodes){
                    if(String(n.id)===String(moveNodeData.targetId)) return NODE_ICONS_DEV[n.node_type]+' '+n.name;
                    const found = findName(n.children||[]);
                    if(found) return found;
                  }
                  return '';
                })(deviceTree)}
              </div>
            )}
            <div style={{overflowY:'auto',flex:1,border:'1px solid #e9ecef',borderRadius:8,padding:'8px'}}>
              {(function MoveTree({nodes, level}){
                return nodes.map(n => {
                  if(n.id===moveNodeData.node.id || n.node_type==='device') return null;
                  const isSelected = String(moveNodeData.targetId)===String(n.id);
                  return (
                    <div key={n.id}>
                      <div onClick={()=>setMoveNodeData(d=>({...d,targetId:String(n.id)}))}
                        style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',marginLeft:level*18,
                          borderRadius:6,marginBottom:2,cursor:'pointer',
                          background: isSelected ? '#2D5C3F' : '#f8f9fa',
                          border: isSelected ? '2px solid #2D5C3F' : '2px solid transparent',
                          color: isSelected ? '#fff' : '#1a1a1a'}}
                        onMouseEnter={e=>{if(!isSelected)e.currentTarget.style.background='#E8F5E9'}}
                        onMouseLeave={e=>{if(!isSelected)e.currentTarget.style.background='#f8f9fa'}}>
                        <span style={{fontSize:14}}>{NODE_ICONS_DEV[n.node_type]}</span>
                        <span style={{fontSize:13,fontWeight:n.node_type!=='device'?700:400}}>{n.name}</span>
                        {n.children&&n.children.length>0&&<span style={{fontSize:11,color:isSelected?'rgba(255,255,255,0.7)':'#999',marginLeft:'auto'}}>{n.children.length} child(ren)</span>}
                      </div>
                      {n.children&&n.children.length>0&&<MoveTree nodes={n.children} level={level+1}/>}
                    </div>
                  );
                });
              })({nodes:deviceTree, level:0})}
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:16}}>
              <button onClick={()=>setMoveNodeData(null)} style={{padding:'9px 20px',border:'1px solid #ddd',borderRadius:8,background:'#f5f5f5',cursor:'pointer',fontWeight:600,fontSize:13}}>Cancel</button>
              <button disabled={!moveNodeData.targetId} onClick={async()=>{
                try{
                  const token=localStorage.getItem('token');
                  const res=await fetch('https://api.safecorepest.com/api/facility-structure/nodes/'+moveNodeData.node.id+'/move',{
                    method:'PUT',headers:{'Content-Type':'application/json','Authorization':'Bearer '+token},
                    body:JSON.stringify({new_parent_id:parseInt(moveNodeData.targetId)})
                  });
                  const data=await res.json();
                  if(!res.ok)throw new Error(data.message||'Error');
                  setMoveNodeData(null);
                  fetchDeviceTree(selectedFacility.id);
                }catch(e){toast.error(e.message);}
              }} style={{padding:'9px 20px',border:'none',borderRadius:8,background:moveNodeData.targetId?'#2D5C3F':'#ccc',color:'#fff',cursor:moveNodeData.targetId?'pointer':'default',fontWeight:700,fontSize:13}}>Move Here</button>
            </div>
          </div>
        </div>
      )}
      {nodeConfirm && (
        <ConfirmDialog
          message={`Delete "${nodeConfirm.node.name}"? This cannot be undone.`}
          confirmText="Yes, Delete"
          confirmColor="#DC2626"
          icon="🗑️"
          onConfirm={() => { nodeConfirm.cb(); setNodeConfirm(null); }}
          onCancel={() => setNodeConfirm(null)}
        />
      )}
      {/* ── Facility Documents ─────────────────────────────────────────────── */}
      <div style={{ ...S.card, padding:0, overflow:'hidden', marginBottom:16 }}>
        <div onClick={() => setShowFacilityDocs(d => !d)}
          style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 20px',
            background: showFacilityDocs ? '#1565C0' : '#F9FAFB', cursor:'pointer',
            borderBottom: showFacilityDocs ? '2px solid #0D47A1' : 'none' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <h3 style={{ margin:0, fontSize:16, fontWeight:700, color: showFacilityDocs ? '#FFF' : '#1565C0' }}>
              📄 Documents
            </h3>
            <span style={{ fontSize:12, fontWeight:600, padding:'3px 10px', borderRadius:12,
              background: showFacilityDocs ? 'rgba(255,255,255,0.2)' : '#E3F2FD',
              color: showFacilityDocs ? '#FFF' : '#1565C0' }}>
              {facilityDocs.length} file{facilityDocs.length !== 1 ? 's' : ''}
            </span>
          </div>
          <span style={{ fontSize:20, color: showFacilityDocs ? '#D4AF37' : '#9CA3AF', fontWeight:'bold' }}>
            {showFacilityDocs ? '▲' : '▼'}
          </span>
        </div>
        {showFacilityDocs && (
          <div style={{ padding:'16px 20px' }}>
            <div style={{ display:'flex', gap:8, marginBottom:10, alignItems:'center', flexWrap:'wrap', justifyContent:'space-between' }}>
              <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                <select id="upload-doc-type" defaultValue="other"
                  style={{ padding:'7px 10px', borderRadius:6, border:'1px solid #D1D5DB', fontSize:12, color:'#374151' }}>
                  <option value="plot_plan">🗺 Plot Plan</option>
                  <option value="service_scope">📋 Service Scope</option>
                  <option value="complaint_log">📝 Complaint Log</option>
                  <option value="signed_agreement">✍️ Signed Agreement</option>
                  <option value="certificate">🏆 Certificate</option>
                  <option value="other">📄 Other</option>
                </select>
                <label style={{ padding:'7px 14px', background:'#1565C0', color:'#FFF', borderRadius:6,
                  cursor:'pointer', fontSize:12, fontWeight:600, whiteSpace:'nowrap' }}>
                  + Upload
                  <input type="file" accept=".pdf,.doc,.docx,.jpg,.png" style={{ display:'none' }}
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const docType = document.getElementById('upload-doc-type').value;
                      const reader = new FileReader();
                      reader.onload = async () => {
                        try {
                          await api.post('/facility-docs/' + selectedFacility.id + '/documents', {
                            name: file.name, doc_type: docType, file_data: reader.result
                          });
                          const r = await api.get('/facility-docs/' + selectedFacility.id + '/documents');
                          setFacilityDocs(r.data.data || []);
                          toast.success('Document uploaded');
                        } catch (err) { toast.error('Upload error: ' + err.message); }
                      };
                      reader.readAsDataURL(file);
                    }} />
                </label>
              </div>
              {selectedDocIds.length > 0 && (
                <button onClick={() => setShowBulkEmail(true)}
                  style={{ padding:'7px 14px', background:'#2D5C3F', color:'#FFF', borderRadius:6,
                    fontSize:12, fontWeight:600, border:'none', cursor:'pointer' }}>
                  📧 Send {selectedDocIds.length} doc{selectedDocIds.length>1?'s':''} by email
                </button>
              )}
            </div>
            {/* Bulk email panel */}
            {showBulkEmail && (
              <div style={{ background:'#F0F7F4', border:'1px solid #C8E6C9', borderRadius:8, padding:'14px', marginBottom:12 }}>
                <div style={{ fontSize:12, fontWeight:600, color:'#2D5C3F', marginBottom:10 }}>
                  Send {selectedDocIds.length} document{selectedDocIds.length>1?'s':''} to:
                </div>
                {/* Preset recipients */}
                {(selectedFacility?.site_contact_email || recipients.length > 0) && (
                  <div style={{ marginBottom:10 }}>
                    <div style={{ fontSize:11, color:'#6B7280', marginBottom:6, fontWeight:500 }}>Facility contacts:</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                      {selectedFacility?.site_contact_email && (
                        <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:12 }}>
                          <input type="checkbox"
                            checked={selectedEmailRecipients.includes(selectedFacility.site_contact_email)}
                            onChange={e => setSelectedEmailRecipients(prev =>
                              e.target.checked ? [...prev, selectedFacility.site_contact_email] : prev.filter(x => x !== selectedFacility.site_contact_email)
                            )} />
                          <span style={{ color:'#374151' }}>{selectedFacility.site_contact_email}</span>
                          <span style={{ fontSize:11, color:'#9CA3AF' }}>(primary)</span>
                        </label>
                      )}
                      {recipients.map(r => (
                        <label key={r.id} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:12 }}>
                          <input type="checkbox"
                            checked={selectedEmailRecipients.includes(r.email)}
                            onChange={e => setSelectedEmailRecipients(prev =>
                              e.target.checked ? [...prev, r.email] : prev.filter(x => x !== r.email)
                            )} />
                          <span style={{ color:'#374151' }}>{r.email}</span>
                          {r.label && <span style={{ fontSize:11, color:'#9CA3AF' }}>({r.label})</span>}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ marginBottom:10 }}>
                  <div style={{ fontSize:11, color:'#6B7280', marginBottom:5, fontWeight:500 }}>Additional recipient:</div>
                  <input type="email" value={bulkEmailTo} onChange={e => setBulkEmailTo(e.target.value)}
                    placeholder="external@example.com"
                    style={{ width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid #C8E6C9', fontSize:12, boxSizing:'border-box' }} />
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button disabled={bulkEmailSending || (selectedEmailRecipients.length===0 && !bulkEmailTo.trim())}
                    onClick={async () => {
                      const allTo = [...selectedEmailRecipients];
                      if (bulkEmailTo.trim()) allTo.push(bulkEmailTo.trim());
                      if (!allTo.length) return;
                      setBulkEmailSending(true);
                      try {
                        const token = localStorage.getItem('token');
                        for (const to of allTo) {
                          const r = await fetch('https://api.safecorepest.com/api/complaint-logs/bulk-email',
                            { method:'POST', headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
                              body: JSON.stringify({ doc_ids: selectedDocIds, to }) });
                          const d = await r.json();
                          if (!d.success) throw new Error(d.error);
                        }
                        toast.success(`${selectedDocIds.length} doc${selectedDocIds.length>1?'s':''} sent to ${allTo.length} recipient${allTo.length>1?'s':''}`);
                        setShowBulkEmail(false); setSelectedDocIds([]); setBulkEmailTo(''); setSelectedEmailRecipients([]);
                        const rd = await api.get('/facility-docs/'+selectedFacility.id+'/documents');
                        setFacilityDocs(rd.data.data || []);
                      } catch(e) { toast.error('Error: '+e.message); }
                      setBulkEmailSending(false);
                    }}
                    style={{ padding:'7px 16px', background:'#2D5C3F', color:'#FFF', border:'none',
                      borderRadius:6, fontSize:12, fontWeight:600, cursor:'pointer',
                      opacity: bulkEmailSending||(selectedEmailRecipients.length===0&&!bulkEmailTo.trim()) ? 0.5 : 1 }}>
                    {bulkEmailSending ? 'Sending...' : `Send to ${selectedEmailRecipients.length+(bulkEmailTo.trim()?1:0)} recipient${selectedEmailRecipients.length+(bulkEmailTo.trim()?1:0)!==1?'s':''}`}
                  </button>
                  <button onClick={() => { setShowBulkEmail(false); setBulkEmailTo(''); setSelectedEmailRecipients([]); }}
                    style={{ padding:'7px 12px', background:'#F3F4F6', color:'#374151',
                      border:'1px solid #D1D5DB', borderRadius:6, fontSize:12, cursor:'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {facilityDocs.length === 0 ? (
              <div style={{ textAlign:'center', padding:'24px', color:'#999', fontSize:13 }}>
                No documents uploaded yet
              </div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ borderBottom:'1px solid #E5E7EB' }}>
                    {['','Name','Type','Status','Size','Date',''].map(h => (
                      <th key={h} style={{ padding:'8px 10px', color:'#6B7280', fontWeight:600, textAlign:'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {facilityDocs.map(d => {
                    const docMeta = {
                      plot_plan:        { label:'Plot Plan',        bg:'#EDE9FE', color:'#5B21B6' },
                      service_scope:    { label:'Service Scope',    bg:'#FEF3C7', color:'#92400E' },
                      complaint_log:    { label:'Complaint Log',    bg:'#E8F5E9', color:'#2D5C3F' },
                      signed_agreement: { label:'Signed Agreement', bg:'#DCFCE7', color:'#166534' },
                      certificate:      { label:'Certificate',      bg:'#FFF7ED', color:'#C2410C' },
                      other:            { label:'Other',            bg:'#F3F4F6', color:'#374151' },
                    }[d.doc_type] || { label: d.doc_type, bg:'#F3F4F6', color:'#374151' };
                    const statusMeta = {
                      draft:    { label:'Draft',    bg:'#F3F4F6', color:'#6B7280' },
                      approved: { label:'Approved', bg:'#D1FAE5', color:'#065F46' },
                      sent:     { label:'Sent',     bg:'#DBEAFE', color:'#1D4ED8' },
                    }[d.status] || { label:'Draft', bg:'#F3F4F6', color:'#6B7280' };
                    const isSelected = selectedDocIds.includes(d.id);
                    return (
                      <tr key={d.id} style={{ borderBottom:'1px solid #F3F4F6',
                        background: isSelected ? '#F0F7F4' : 'white' }}>
                        <td style={{ padding:'9px 6px', width:32, textAlign:'center' }}>
                          <input type="checkbox" checked={isSelected}
                            onChange={e => setSelectedDocIds(prev =>
                              e.target.checked ? [...prev, d.id] : prev.filter(x => x !== d.id)
                            )} />
                        </td>
                        <td style={{ padding:'9px 10px', fontWeight:600, color:'#1A3A2A', fontSize:12 }}>📄 {d.name}</td>
                        <td style={{ padding:'9px 10px' }}>
                          <span style={{ padding:'2px 7px', borderRadius:10, fontSize:10, fontWeight:600,
                            background: docMeta.bg, color: docMeta.color }}>
                            {docMeta.label}
                          </span>
                        </td>
                        <td style={{ padding:'9px 10px' }}>
                          <span style={{ padding:'2px 7px', borderRadius:10, fontSize:10, fontWeight:600,
                            background: statusMeta.bg, color: statusMeta.color }}>
                            {statusMeta.label}
                          </span>
                        </td>
                        <td style={{ padding:'9px 10px', color:'#9CA3AF', fontSize:11 }}>
                          {d.file_size ? (d.file_size/1024).toFixed(0)+' KB' : '—'}
                        </td>
                        <td style={{ padding:'9px 10px', color:'#9CA3AF', fontSize:11 }}>
                          {new Date(d.created_at).toLocaleDateString()}
                        </td>
                        <td style={{ padding:'9px 10px' }}>
                          <div style={{ display:'flex', gap:5 }}>
                            <button onClick={async () => {
                              try {
                                const r = await api.get('/facility-docs/documents/'+d.id+'/download', { responseType:'blob' });
                                const url = window.URL.createObjectURL(new Blob([r.data], { type:'application/pdf' }));
                                setDocBlobUrl(url); setViewingDoc(d); setShowEmailInput(false); setEmailTo('');
                              } catch { toast.error('Error loading document'); }
                            }} style={{ padding:'3px 8px', background:'#E3F2FD', color:'#1565C0',
                              border:'none', borderRadius:4, cursor:'pointer', fontSize:11, fontWeight:600 }}>View</button>
                            <button onClick={() => setConfirmDialog({
                              message: 'Delete "' + d.name + '"? This cannot be undone.',
                              confirmText:'Delete', icon:'🗑',
                              onConfirm: async () => {
                                setConfirmDialog(null);
                                try {
                                  await api.delete('/facility-docs/documents/'+d.id);
                                  const r = await api.get('/facility-docs/'+selectedFacility.id+'/documents');
                                  setFacilityDocs(r.data.data || []);
                                  setSelectedDocIds(prev => prev.filter(x => x !== d.id));
                                } catch { toast.error('Delete error'); }
                              }, onCancel: () => setConfirmDialog(null)
                            })} style={{ padding:'3px 8px', background:'#FEF2F2', color:'#DC2626',
                              border:'none', borderRadius:4, cursor:'pointer', fontSize:11, fontWeight:600 }}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Create Client Choice Modal */}
      {showCreateChoice && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => { if (e.target === e.currentTarget) setShowCreateChoice(false); }}>
          <div style={{ background:'white', borderRadius:'12px', maxWidth:'550px', width:'95%', overflow:'hidden' }}>
            <div style={{ background:'linear-gradient(135deg, #2D5C3F, #1A3A2A)', padding:'20px 24px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ color:'white', fontSize:'16px', fontWeight:'700' }}>New Client</div>
                <div style={{ color:'rgba(255,255,255,0.6)', fontSize:'12px' }}>How would you like to create this client?</div>
              </div>
              <button onClick={() => setShowCreateChoice(false)} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.7)', fontSize:'20px', cursor:'pointer' }}>&times;</button>
            </div>
            <div style={{ padding:'24px', display:'flex', flexDirection:'column', gap:'16px' }}>
              <div style={{ border:'2px dashed #C8E6C9', borderRadius:'10px', padding:'24px', textAlign:'center', cursor:'pointer', transition:'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor='#2D5C3F'; e.currentTarget.style.background='#F0F7F4'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor='#C8E6C9'; e.currentTarget.style.background='white'; }}>
                <div style={{ fontSize:'32px', marginBottom:'8px' }}>&#128196;</div>
                <div style={{ fontSize:'15px', fontWeight:'700', color:'#2D5C3F', marginBottom:'4px' }}>Upload Signed Proposal</div>
                <div style={{ fontSize:'12px', color:'#888', marginBottom:'12px' }}>Upload a scanned signed proposal PDF. The system will automatically identify the proposal and pre-fill client data.</div>
                <label style={{ display:'inline-block', padding:'10px 24px', background:'#2D5C3F', color:'white', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'600', opacity: uploadingScanned ? 0.6 : 1 }}>
                  {uploadingScanned ? 'Processing PDF...' : 'Choose PDF File'}
                  <input type="file" accept=".pdf" style={{ display:'none' }} disabled={uploadingScanned}
                    onChange={e => handleScannedUpload(e.target.files[0])} />
                </label>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                <div style={{ flex:1, height:'1px', background:'#E0E0E0' }}></div>
                <span style={{ fontSize:'12px', color:'#999', fontWeight:'600' }}>OR</span>
                <div style={{ flex:1, height:'1px', background:'#E0E0E0' }}></div>
              </div>
              <button onClick={startFromScratch}
                style={{ border:'1px solid #E0E0E0', borderRadius:'10px', padding:'20px', textAlign:'center', cursor:'pointer', background:'white', transition:'all 0.2s', width:'100%' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor='#1565C0'; e.currentTarget.style.background='#E3F2FD'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor='#E0E0E0'; e.currentTarget.style.background='white'; }}>
                <div style={{ fontSize:'32px', marginBottom:'8px' }}>&#9999;&#65039;</div>
                <div style={{ fontSize:'15px', fontWeight:'700', color:'#1565C0' }}>Create from Scratch</div>
                <div style={{ fontSize:'12px', color:'#888', marginTop:'4px' }}>Manually enter all client information without linking to a proposal.</div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {viewingDoc && docBlobUrl && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={(e) => { if (e.target === e.currentTarget) { URL.revokeObjectURL(docBlobUrl); setViewingDoc(null); setDocBlobUrl(null); } }}>
          <div style={{ background:'white', borderRadius:'12px', width:'95%', maxWidth:'900px', height:'90vh', display:'flex', flexDirection:'column', overflow:'hidden' }}>
            {/* Header */}
            <div style={{ padding:'14px 20px', borderBottom:'2px solid #E0E0E0', display:'flex', justifyContent:'space-between', alignItems:'center', background:'#2D5C3F' }}>
              <div>
                <div style={{ color:'white', fontSize:'15px', fontWeight:'700' }}>{viewingDoc.name}</div>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:4 }}>
                  <span style={{ fontSize:'11px', color:'rgba(255,255,255,0.6)' }}>
                    {viewingDoc.doc_type?.replace(/_/g,' ')} &bull; {viewingDoc.file_size ? (viewingDoc.file_size/1024).toFixed(0)+' KB' : ''} &bull; {new Date(viewingDoc.created_at).toLocaleDateString()}
                  </span>
                  {/* Status badge + toggle */}
                  <select value={viewingDoc.status || 'draft'}
                    onChange={async (e) => {
                      const newStatus = e.target.value;
                      try {
                        const token = localStorage.getItem('token');
                        await fetch(`https://api.safecorepest.com/api/complaint-logs/docs/${viewingDoc.id}/status`,
                          { method:'PATCH', headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
                            body: JSON.stringify({ status: newStatus }) });
                        setViewingDoc(v => ({...v, status: newStatus}));
                        setFacilityDocs(prev => prev.map(d => d.id === viewingDoc.id ? {...d, status: newStatus} : d));
                        toast.success('Status updated to ' + newStatus);
                      } catch { toast.error('Error updating status'); }
                    }}
                    style={{ fontSize:11, padding:'2px 6px', borderRadius:6, border:'none', fontWeight:600, cursor:'pointer',
                      background: viewingDoc.status==='sent' ? '#DBEAFE' : viewingDoc.status==='approved' ? '#D1FAE5' : '#F3F4F6',
                      color: viewingDoc.status==='sent' ? '#1D4ED8' : viewingDoc.status==='approved' ? '#065F46' : '#6B7280' }}>
                    <option value="draft">Draft</option>
                    <option value="approved">Approved</option>
                    <option value="sent">Sent</option>
                  </select>
                </div>
              </div>
              <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
                <button onClick={() => {
                  const link = document.createElement('a'); link.href = docBlobUrl; link.download = viewingDoc.name; link.click();
                }} style={{ padding:'7px 14px', background:'#E3F2FD', color:'#1565C0', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600' }}>
                  Download
                </button>
                <button onClick={() => {
                  const w = window.open(docBlobUrl, '_blank');
                  if (w) setTimeout(() => w.print(), 500);
                }} style={{ padding:'7px 14px', background:'#FFF3E0', color:'#E65100', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600' }}>
                  Print
                </button>
                <button onClick={() => setShowEmailInput(!showEmailInput)}
                  style={{ padding:'7px 14px', background: showEmailInput ? '#C8E6C9' : '#E8F5E9', color:'#2E7D32', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600' }}>
                  📧 Send Email
                </button>
                <button onClick={() => { URL.revokeObjectURL(docBlobUrl); setViewingDoc(null); setDocBlobUrl(null); setShowEmailInput(false); setEmailTo(''); }}
                  style={{ background:'rgba(255,255,255,0.2)', color:'white', border:'1px solid rgba(255,255,255,0.4)', borderRadius:'6px', padding:'7px 12px', cursor:'pointer', fontSize:'16px' }}>
                  ✕
                </button>
              </div>
            </div>
            {/* Email panel */}
            {showEmailInput && (
              <div style={{ padding:'14px 20px', background:'#F0F7F4', borderBottom:'1px solid #C8E6C9' }}>
                <div style={{ fontSize:12, fontWeight:600, color:'#2D5C3F', marginBottom:10 }}>
                  Send "{viewingDoc.name}" to:
                </div>
                {/* Preset recipients from facility */}
                {(selectedFacility?.site_contact_email || recipients.length > 0) && (
                  <div style={{ marginBottom:10 }}>
                    <div style={{ fontSize:11, color:'#6B7280', marginBottom:6, fontWeight:500 }}>Facility contacts:</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                      {selectedFacility?.site_contact_email && (
                        <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13 }}>
                          <input type="checkbox"
                            checked={selectedEmailRecipients.includes(selectedFacility.site_contact_email)}
                            onChange={e => setSelectedEmailRecipients(prev =>
                              e.target.checked
                                ? [...prev, selectedFacility.site_contact_email]
                                : prev.filter(x => x !== selectedFacility.site_contact_email)
                            )} />
                          <span style={{ color:'#374151' }}>{selectedFacility.site_contact_email}</span>
                          <span style={{ fontSize:11, color:'#9CA3AF' }}>(primary contact)</span>
                        </label>
                      )}
                      {recipients.map(r => (
                        <label key={r.id} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13 }}>
                          <input type="checkbox"
                            checked={selectedEmailRecipients.includes(r.email)}
                            onChange={e => setSelectedEmailRecipients(prev =>
                              e.target.checked ? [...prev, r.email] : prev.filter(x => x !== r.email)
                            )} />
                          <span style={{ color:'#374151' }}>{r.email}</span>
                          {r.label && <span style={{ fontSize:11, color:'#9CA3AF' }}>({r.label})</span>}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {/* Additional / external recipient */}
                <div style={{ marginBottom:10 }}>
                  <div style={{ fontSize:11, color:'#6B7280', marginBottom:5, fontWeight:500 }}>
                    Additional recipient (optional):
                  </div>
                  <input type="email" placeholder="external@example.com"
                    value={extraEmailTo} onChange={e => setExtraEmailTo(e.target.value)}
                    style={{ width:'100%', padding:'8px 12px', border:'1px solid #C8E6C9',
                      borderRadius:'6px', fontSize:13, boxSizing:'border-box' }} />
                </div>
                {/* Send / Cancel */}
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <button disabled={sendingEmail || (selectedEmailRecipients.length === 0 && !extraEmailTo.trim())}
                    onClick={async () => {
                      const allRecipients = [...selectedEmailRecipients];
                      if (extraEmailTo.trim()) allRecipients.push(extraEmailTo.trim());
                      if (!allRecipients.length) return;
                      setSendingEmail(true);
                      try {
                        // Send to each recipient
                        for (const to of allRecipients) {
                          await api.post('/facility-docs/documents/' + viewingDoc.id + '/send-email', { to });
                        }
                        toast.success(`Document sent to ${allRecipients.length} recipient${allRecipients.length>1?'s':''}`);
                        // Update status
                        const token = localStorage.getItem('token');
                        await fetch(`https://api.safecorepest.com/api/complaint-logs/docs/${viewingDoc.id}/status`,
                          { method:'PATCH', headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
                            body: JSON.stringify({ status:'sent' }) });
                        const sentTo = allRecipients.join(', ');
                        setViewingDoc(v => ({...v, status:'sent', sent_to: sentTo}));
                        setFacilityDocs(prev => prev.map(d => d.id === viewingDoc.id ? {...d, status:'sent', sent_to: sentTo} : d));
                        setShowEmailInput(false);
                        setSelectedEmailRecipients([]);
                        setExtraEmailTo('');
                      } catch (err) { toast.error('Error sending: ' + (err.response?.data?.message || err.message)); }
                      setSendingEmail(false);
                    }}
                    style={{ padding:'8px 20px', background:'#2D5C3F', color:'white', border:'none',
                      borderRadius:'6px', cursor:'pointer', fontSize:'13px', fontWeight:'600',
                      opacity: sendingEmail||(selectedEmailRecipients.length===0&&!extraEmailTo.trim()) ? 0.5 : 1 }}>
                    {sendingEmail ? 'Sending...' : `Send${selectedEmailRecipients.length+!!extraEmailTo.trim()>0?' to '+(selectedEmailRecipients.length+(extraEmailTo.trim()?1:0))+' recipient'+(selectedEmailRecipients.length+(extraEmailTo.trim()?1:0)>1?'s':''):''}` }
                  </button>
                  <button onClick={() => { setShowEmailInput(false); setSelectedEmailRecipients([]); setExtraEmailTo(''); }}
                    style={{ padding:'8px 12px', background:'#F3F4F6', color:'#374151',
                      border:'1px solid #D1D5DB', borderRadius:'6px', cursor:'pointer', fontSize:'13px' }}>
                    Cancel
                  </button>
                  {viewingDoc.sent_to && (
                    <span style={{ fontSize:11, color:'#6B7280', marginLeft:8 }}>
                      Last sent to: {viewingDoc.sent_to}
                    </span>
                  )}
                </div>
              </div>
            )}
            <div style={{ flex:1 }}>
              <iframe title="doc-viewer" src={docBlobUrl} style={{ width:'100%', height:'100%', border:'none' }} />
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showFacilityModal && renderFacilityModal()}
      {showActivateModal && activateProposal && (
        <ActivateServicesModal
          proposal={activateProposal}
          onSuccess={(result) => {
            setShowActivateModal(false);
            setActivateProposal(null);
            toast.success(result.message || 'Services activated successfully!');
          }}
          onSkip={() => {
            setShowActivateModal(false);
            setActivateProposal(null);
          }}
        />
      )}
      {showProductModal && renderProductModal()}
      {showSOModal && selectedFacility && (
        <ServiceOrderModal isOpen={showSOModal} order={selectedOrder} facilityId={selectedFacility.id}
          facilityName={selectedFacility.name} clientName={selectedClient?.name}
          onClose={() => { setShowSOModal(false); setSelectedOrder(null); }}
          onSuccess={() => { loadFacility(selectedFacility); setShowSOModal(false); setSelectedOrder(null); }} />
      )}
      {/* Phase 2: Stop Recurring Modal — independent sibling, self-manages visibility via isOpen */}
      <StopRecurringModal
        isOpen={showStopRecurringModal}
        onClose={() => setShowStopRecurringModal(false)}
        onSuccess={() => {
          setShowStopRecurringModal(false);
          if (selectedFacility) loadFacility(selectedFacility);
        }}
        recurringService={stopRecurringTarget}
      />
      {/* Phase 3: Edit Recurrence Pattern Modal */}
      <EditRecurrencePatternModal
        isOpen={showEditPatternModal}
        onClose={() => setShowEditPatternModal(false)}
        onSuccess={() => {
          setShowEditPatternModal(false);
          if (selectedFacility) loadFacility(selectedFacility);
        }}
        recurringService={editPatternTarget}
      />
      {/* ===== REPORT PREVIEW MODAL ===== */}
      {reportPreview && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}
          onClick={e => { if (e.target === e.currentTarget) setReportPreview(null); }}>
          <div style={{ background:'#FFF', borderRadius:'12px', width:'100%', maxWidth:'860px', height:'90vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 25px 60px rgba(0,0,0,0.4)' }}>
            <div style={{ padding:'18px 24px', background:'linear-gradient(135deg, #1F4027, #2D5C3F)', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
              <div>
                <h2 style={{ margin:0, color:'#FFF', fontSize:'17px', fontWeight:'700' }}>Service Report Preview</h2>
                <p style={{ margin:'3px 0 0', color:'#D4AF37', fontSize:'13px' }}>{reportPreview.order.order_number} · {new Date(reportPreview.order.scheduled_date+'T00:00:00').toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' })}</p>
              </div>
              <div style={{ display:'flex', gap:'8px' }}>
                {reportPreview.pdfUrl && (
                  <a href={reportPreview.pdfUrl} download={reportPreview.order.order_number + '_report.pdf'} target="_blank" rel="noreferrer"
                    style={{ background:'#D4AF37', border:'none', color:'#1A3A2A', padding:'8px 16px', borderRadius:'6px', cursor:'pointer', fontSize:'14px', fontWeight:'700', textDecoration:'none', display:'flex', alignItems:'center', gap:'6px' }}>
                    ⬇ Download PDF
                  </a>
                )}
                <button onClick={() => setReportPreview(null)}
                  style={{ background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.3)', color:'#FFF', padding:'8px 16px', borderRadius:'6px', cursor:'pointer', fontSize:'14px', fontWeight:'600' }}>
                  ✕ Close
                </button>
              </div>
            </div>
            <div style={{ display:'flex', borderBottom:'2px solid rgba(255,255,255,0.2)', background:'linear-gradient(135deg, #1F4027, #2D5C3F)', flexShrink:0 }}>
              {[{key:'report',label:'📄 Report'},{key:'history',label:'🕐 History'}].map(tab=>(
                <button key={tab.key} onClick={async()=>{
                  setReportPreviewTab(tab.key);
                  if(tab.key==='history'&&reportHistory.length===0){
                    setReportHistoryLoading(true);
                    try{const res=await api.get(`/activity-log/entity/work_order/${reportPreview.order.id}`);const d=res.data||res;setReportHistory(d.data||[]);}catch(e){}
                    setReportHistoryLoading(false);
                  }
                }} style={{padding:'10px 22px',border:'none',cursor:'pointer',fontWeight:600,fontSize:13,background:'transparent',
                  borderBottom:reportPreviewTab===tab.key?'3px solid #D4AF37':'3px solid transparent',
                  color:reportPreviewTab===tab.key?'#D4AF37':'rgba(255,255,255,0.7)'}}>
                  {tab.label}
                </button>
              ))}
            </div>
            <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', background:'#F8FAFC' }}>
              {reportPreviewTab==='history' ? (
                <div style={{padding:'20px 24px',overflowY:'auto',flex:1}}>
                  {reportHistoryLoading?(<div style={{textAlign:'center',padding:40,color:'#999'}}>Loading...</div>)
                  :reportHistory.length===0?(<div style={{textAlign:'center',padding:40,color:'#999'}}>No activity recorded yet</div>)
                  :(
                    <div style={{position:'relative',paddingLeft:8}}>
                      <div style={{position:'absolute',left:24,top:0,bottom:0,width:2,background:'#e9ecef'}}/>
                      {reportHistory.map(log=>{
                        const ac={created:'#2D5C3F',updated:'#1565C0',completed:'#2D5C3F',started:'#E65100',cancelled:'#B71C1C',rescheduled:'#6A1B9A',reassigned:'#00695C',report_queued:'#E65100',report_released:'#2D5C3F',report_rejected:'#B71C1C'};
                        const color=ac[log.action]||'#999';
                        return(<div key={log.id} style={{display:'flex',gap:16,marginBottom:20}}>
                          <div style={{width:32,height:32,borderRadius:'50%',flexShrink:0,background:color,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,color:'#fff',fontWeight:700}}>
                            {log.source==='mobile'?'📱':log.performed_by_name?'👤':'⚙️'}
                          </div>
                          <div style={{flex:1,background:'#f8f9fa',borderRadius:8,padding:'10px 14px',border:'1px solid #e9ecef'}}>
                            <div style={{display:'flex',justifyContent:'space-between'}}>
                              <span style={{fontSize:12,fontWeight:700,textTransform:'uppercase',color}}>{log.action?.replace(/_/g,' ')}</span>
                              <span style={{fontSize:11,color:'#999'}}>{new Date(log.performed_at).toLocaleString('en-US',{month:'short',day:'numeric',year:'numeric',hour:'numeric',minute:'2-digit',hour12:true})}</span>
                            </div>
                            <div style={{fontSize:13,color:'#333',marginTop:4,fontWeight:600}}>
                              {log.performed_by_name||'System'}
                              {log.performed_by_role&&<span style={{fontWeight:400,color:'#666'}}> · {log.performed_by_role}</span>}
                              {log.source&&<span style={{fontWeight:400,color:'#999'}}> · {log.source==='mobile'?'Mobile App':log.source==='system'?'System':'Admin'}</span>}
                            </div>
                            {log.notes&&<div style={{fontSize:12,color:'#666',marginTop:4}}>{log.notes}</div>}
                          </div>
                        </div>);
                      })}
                    </div>
                  )}
                </div>
              ) : reportPreview.pdfUrl ? (
                <iframe src={reportPreview.pdfUrl} style={{ flex:1, border:'none', width:'100%', height:'100%', display:'block' }} title="Service Report PDF" scrolling="yes" />
              ) : reportPreviewLoading ? (
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'200px', gap:'16px' }}>
                  <div style={{ width:'40px', height:'40px', border:'4px solid #E5E7EB', borderTopColor:'#2D5C3F', borderRadius:'50%', animation:'spin 1s linear infinite' }} />
                  <p style={{ color:'#6B7280', fontSize:'14px' }}>Loading report...</p>
                </div>
              ) : reportPreview.data ? (() => {
                const rpt = reportPreview.data;
                const h = rpt.header || {};
                const areas = rpt.areas || [];
                const products = rpt.products_applied || [];
                return (
                  <div style={{ maxWidth:'760px', margin:'0 auto' }}>
                    <div style={{ background:'#FFF', borderRadius:'10px', padding:'20px', marginBottom:'16px', border:'1px solid #E5E7EB', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                        <div><p style={{ margin:'0 0 4px', fontSize:'11px', color:'#9CA3AF', fontWeight:'700', textTransform:'uppercase' }}>Facility</p><p style={{ margin:0, fontWeight:'600', color:'#111827' }}>{h.facility?.name || '—'}</p></div>
                        <div><p style={{ margin:'0 0 4px', fontSize:'11px', color:'#9CA3AF', fontWeight:'700', textTransform:'uppercase' }}>Technician</p><p style={{ margin:0, fontWeight:'600', color:'#111827' }}>{h.technician || '—'}</p></div>
                        <div><p style={{ margin:'0 0 4px', fontSize:'11px', color:'#9CA3AF', fontWeight:'700', textTransform:'uppercase' }}>Service Date</p><p style={{ margin:0, fontWeight:'600', color:'#111827' }}>{h.service_date || '—'}</p></div>
                        <div><p style={{ margin:'0 0 4px', fontSize:'11px', color:'#9CA3AF', fontWeight:'700', textTransform:'uppercase' }}>Duration</p><p style={{ margin:0, fontWeight:'600', color:'#111827' }}>{h.service_start && h.service_end ? calcDuration(h.service_start, h.service_end) : '—'}</p></div>
                      </div>
                      {rpt.technician_notes && (
                        <div style={{ marginTop:'14px', padding:'12px', background:'#F0F7F4', borderRadius:'8px', borderLeft:'4px solid #2D5C3F' }}>
                          <p style={{ margin:'0 0 4px', fontSize:'11px', color:'#2D5C3F', fontWeight:'700', textTransform:'uppercase' }}>Technician Notes</p>
                          <p style={{ margin:0, fontSize:'13px', color:'#374151', lineHeight:'1.6' }}>{rpt.technician_notes}</p>
                        </div>
                      )}
                    </div>
                    {areas.length > 0 && (
                      <div style={{ marginBottom:'16px' }}>
                        <h4 style={{ margin:'0 0 10px', color:'#2D5C3F', fontSize:'14px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.05em' }}>Inspection Areas</h4>
                        {areas.map((area, ai) => (
                          <div key={ai} style={{ background:'#FFF', borderRadius:'10px', padding:'16px', marginBottom:'10px', border:'1px solid #E5E7EB' }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: area.devices?.length ? '12px' : 0 }}>
                              <h5 style={{ margin:0, fontSize:'14px', fontWeight:'700', color:'#1F4027' }}>{area.name}</h5>
                              {area.findings?.length > 0 && (
                                <span style={{ fontSize:'12px', color:'#DC2626', fontWeight:'600', background:'#FEE2E2', padding:'2px 10px', borderRadius:'10px' }}>
                                  {area.findings.length} finding{area.findings.length !== 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                            {area.devices?.map((dev, di) => (
                              <div key={di} style={{ padding:'8px 12px', background:'#F9FAFB', borderRadius:'6px', marginBottom:'6px', fontSize:'13px' }}>
                                <span style={{ fontWeight:'600', color:'#374151' }}>{dev.device_type}</span>
                                {dev.location && <span style={{ color:'#9CA3AF', marginLeft:'8px' }}>· {dev.location}</span>}
                                {dev.result && <span style={{ marginLeft:'8px', color: dev.result === 'no_activity' ? '#059669' : '#DC2626', fontWeight:'600' }}>· {dev.result.replace(/_/g,' ')}</span>}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                    {products.length > 0 && (
                      <div style={{ background:'#FFF', borderRadius:'10px', padding:'16px', marginBottom:'16px', border:'1px solid #E5E7EB' }}>
                        <h4 style={{ margin:'0 0 12px', color:'#2D5C3F', fontSize:'14px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.05em' }}>Products Applied</h4>
                        {products.map((p, pi) => (
                          <div key={pi} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #F3F4F6', fontSize:'13px' }}>
                            <span style={{ fontWeight:'600', color:'#374151' }}>{p.product_name || p.name}</span>
                            <span style={{ color:'#6B7280' }}>{p.amount} {p.unit}{p.area_name ? ` · ${p.area_name}` : ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {rpt.risk_score !== undefined && (
                      <div style={{ background:'#FFF', borderRadius:'10px', padding:'16px', border:'1px solid #E5E7EB', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <span style={{ fontWeight:'700', color:'#111827', fontSize:'14px' }}>Risk Score</span>
                        <span style={{ fontWeight:'700', fontSize:'20px', color: rpt.risk_score > 70 ? '#DC2626' : rpt.risk_score > 40 ? '#D97706' : '#059669' }}>
                          {rpt.risk_score}/100
                        </span>
                      </div>
                    )}
                  </div>
                );
              })() : (
                <div style={{ textAlign:'center', padding:'40px', color:'#9CA3AF' }}>Could not load report data</div>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          confirmColor={confirmDialog.confirmColor || '#DC2626'}
          icon={confirmDialog.icon || '⚠️'}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </div>
  );

  // ============================
  // MODAL RENDERERS
  // ============================
  function renderClientModal() {
    return (
      <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', justifyContent:'center', alignItems:'center' }}>
        <div style={{ backgroundColor:'#FFF', borderRadius:'12px', width:'600px', maxHeight:'80vh', overflow:'auto', padding:'32px' }}>
          <h2 style={{ color:'#2D5C3F', margin:'0 0 24px' }}>{editingClient ? 'Edit Client' : 'New Client'}</h2>
          <div style={S.grid2}>
            <div><label style={S.label}>Client Name *</label><input style={S.input} value={clientForm.name} onChange={e => setClientForm({...clientForm, name:e.target.value})} /></div>
            <div><label style={S.label}>Industry</label><input style={S.input} value={clientForm.industry} onChange={e => setClientForm({...clientForm, industry:e.target.value})} /></div>
            <div><label style={S.label}>Contact Email</label><input style={S.input} type="email" placeholder="Primary contact email" value={clientForm.email} onChange={e => setClientForm({...clientForm, email:e.target.value})} /></div>
            <div><label style={S.label}>Billing Email</label><input style={S.input} type="email" placeholder="Main invoicing email" value={clientForm.billing_email||''} onChange={e => setClientForm({...clientForm, billing_email:e.target.value})} /></div>
            <div><label style={S.label}>Phone</label><input style={S.input} value={clientForm.phone} onChange={e => setClientForm({...clientForm, phone:e.target.value})} /></div>
            <div style={{ gridColumn:'1/3' }}><label style={S.label}>Billing Address</label><input style={S.input} value={clientForm.billing_address} onChange={e => setClientForm({...clientForm, billing_address:e.target.value})} /></div>
            <div><label style={S.label}>City</label><input style={S.input} value={clientForm.billing_city} onChange={e => setClientForm({...clientForm, billing_city:e.target.value})} /></div>
            <div><label style={S.label}>State</label><input style={S.input} value={clientForm.billing_state} onChange={e => setClientForm({...clientForm, billing_state:e.target.value})} /></div>
            <div><label style={S.label}>Zip Code</label><input style={S.input} value={clientForm.billing_zip} onChange={e => setClientForm({...clientForm, billing_zip:e.target.value})} /></div>
            <div><label style={S.label}>Contract Start</label><input style={S.input} type="date" value={clientForm.contract_start_date} onChange={e => setClientForm({...clientForm, contract_start_date:e.target.value})} /></div>
            <div><label style={S.label}>Contract End</label><input style={S.input} type="date" value={clientForm.contract_end_date} onChange={e => setClientForm({...clientForm, contract_end_date:e.target.value})} /></div>
          </div>
          {/* Additional Billing Emails */}
          <div style={{ marginTop:'20px', padding:'16px', background:'#F9FAFB', borderRadius:'8px', border:'1px solid #E5E7EB' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
              <label style={{ fontSize:'13px', fontWeight:700, color:'#374151' }}>📧 Additional Billing Emails</label>
              {!showBillingEmailForm && (
                <button onClick={() => { setShowBillingEmailForm(true); setBillingEmailEdit(null); setBillingEmailForm({ email:'', label:'' }); }}
                  style={{ padding:'4px 12px', background:'#F0FDF4', color:'#16A34A', border:'1px solid #BBF7D0', borderRadius:'6px', fontSize:'12px', fontWeight:600, cursor:'pointer' }}>
                  + Add Email
                </button>
              )}
            </div>
            {billingEmails.length === 0 && !showBillingEmailForm && (
              <p style={{ fontSize:'12px', color:'#9CA3AF', margin:0 }}>No additional billing emails. Invoices will be sent to the Billing Email above.</p>
            )}
            {billingEmails.map((be, i) => (
              <div key={be.id||i} style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px', padding:'8px 10px', background:'white', borderRadius:'6px', border:'1px solid #E5E7EB' }}>
                <span style={{ fontSize:'13px', fontWeight:600, color:'#1A3A2A', flex:1 }}>{be.email}</span>
                {be.label && <span style={{ fontSize:'11px', color:'#6B7280', background:'#F3F4F6', padding:'2px 7px', borderRadius:'4px' }}>{be.label}</span>}
                <button onClick={() => { setBillingEmailEdit(be); setBillingEmailForm({ email:be.email, label:be.label||'' }); setShowBillingEmailForm(true); }}
                  style={{ padding:'3px 8px', background:'#EFF6FF', color:'#2563EB', border:'none', borderRadius:'4px', fontSize:'11px', cursor:'pointer' }}>Edit</button>
                <button onClick={() => setBillingEmails(prev => prev.filter((_,j) => j !== i))}
                  style={{ padding:'3px 8px', background:'#FEF2F2', color:'#DC2626', border:'none', borderRadius:'4px', fontSize:'11px', cursor:'pointer' }}>✕</button>
              </div>
            ))}
            {showBillingEmailForm && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginTop:'8px' }}>
                <input type="email" placeholder="email@example.com *" value={billingEmailForm.email}
                  onChange={e => setBillingEmailForm(f=>({...f,email:e.target.value}))}
                  style={{ padding:'8px 10px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'13px' }} />
                <input type="text" placeholder="Label (e.g. AP Dept, Controller)" value={billingEmailForm.label}
                  onChange={e => setBillingEmailForm(f=>({...f,label:e.target.value}))}
                  style={{ padding:'8px 10px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'13px' }} />
                <button onClick={() => {
                  if (!billingEmailForm.email || !billingEmailForm.email.includes('@')) { toast.error('Valid email required'); return; }
                  if (billingEmailEdit) {
                    setBillingEmails(prev => prev.map(b => b.id === billingEmailEdit.id ? {...b, ...billingEmailForm} : b));
                  } else {
                    setBillingEmails(prev => [...prev, { ...billingEmailForm, id: Date.now() }]);
                  }
                  setShowBillingEmailForm(false); setBillingEmailEdit(null);
                }} style={{ padding:'8px', background:'#2D5C3F', color:'white', border:'none', borderRadius:'6px', fontSize:'13px', fontWeight:600, cursor:'pointer' }}>
                  {billingEmailEdit ? 'Update' : 'Add'}
                </button>
                <button onClick={() => { setShowBillingEmailForm(false); setBillingEmailEdit(null); }}
                  style={{ padding:'8px', background:'white', color:'#6B7280', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'13px', cursor:'pointer' }}>
                  Cancel
                </button>
              </div>
            )}
          </div>

          <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', marginTop:'24px' }}>
            <button onClick={() => setShowClientModal(false)} style={S.btnOutline}>Cancel</button>
            <button onClick={saveClient} style={S.btn('#2D5C3F','#FFF')}>{editingClient ? 'Save Changes' : 'Create Client'}</button>
          </div>
        </div>
      </div>
    );
  }

  function renderFacilityModal() {
    return (
      <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', justifyContent:'center', alignItems:'center' }}>
        <div style={{ backgroundColor:'#FFF', borderRadius:'12px', width:'700px', maxHeight:'85vh', overflow:'auto', padding:'32px' }}>
          <h2 style={{ color:'#2D5C3F', margin:'0 0 24px' }}>{editingFacility ? 'Edit Facility' : 'New Facility'}</h2>
          <div style={S.grid2}>
            <div style={{ gridColumn:'1/3' }}><label style={S.label}>Facility Name *</label><input style={S.input} value={facilityForm.name} onChange={e => setFacilityForm({...facilityForm, name:e.target.value})} /></div>
            <div style={{ gridColumn:'1/3' }}><label style={S.label}>Address *</label><input style={S.input} value={facilityForm.address} onChange={e => setFacilityForm({...facilityForm, address:e.target.value})} /></div>
            <div><label style={S.label}>City</label><input style={S.input} value={facilityForm.city} onChange={e => setFacilityForm({...facilityForm, city:e.target.value})} /></div>
            <div><label style={S.label}>State</label><input style={S.input} value={facilityForm.state} onChange={e => setFacilityForm({...facilityForm, state:e.target.value})} /></div>
            <div><label style={S.label}>Zip Code</label><input style={S.input} value={facilityForm.zip_code} onChange={e => setFacilityForm({...facilityForm, zip_code:e.target.value})} /></div>
            <div><label style={S.label}>Facility Type</label>
              <select style={S.input} value={facilityForm.facility_type} onChange={e => setFacilityForm({...facilityForm, facility_type:e.target.value})}>
                <option value="">Select...</option><option value="Manufacturing">Manufacturing</option><option value="Distribution">Distribution</option>
                <option value="Warehouse">Warehouse</option><option value="Restaurant">Restaurant</option><option value="Retail">Retail</option>
                <option value="Office">Office</option><option value="Other">Other</option>
              </select>
            </div>
            <div><label style={S.label}>Square Footage</label><input style={S.input} type="number" value={facilityForm.square_footage} onChange={e => setFacilityForm({...facilityForm, square_footage:e.target.value})} /></div>
            <div><label style={S.label}>Certification Scheme</label>
              <select style={S.input} value={facilityForm.certification_scheme} onChange={e => setFacilityForm({...facilityForm, certification_scheme:e.target.value})}>
                <option value="">None</option><option value="SQF">SQF</option><option value="BRCGS">BRCGS</option>
                <option value="FSSC22000">FSSC 22000</option><option value="AIB">AIB</option><option value="IFS">IFS</option>
              </select>
            </div>
            <div><label style={S.label}>Certification Level</label><input style={S.input} value={facilityForm.certification_level} onChange={e => setFacilityForm({...facilityForm, certification_level:e.target.value})} /></div>
            <div><label style={S.label}>Audit Frequency</label>
              <select style={S.input} value={facilityForm.audit_frequency} onChange={e => setFacilityForm({...facilityForm, audit_frequency:e.target.value})}>
                <option value="">Select...</option><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option>
                <option value="semi-annual">Semi-Annual</option><option value="annual">Annual</option>
              </select>
            </div>
            <div><label style={S.label}>Next Audit Date</label><input style={S.input} type="date" value={facilityForm.next_audit_date} onChange={e => setFacilityForm({...facilityForm, next_audit_date:e.target.value})} /></div>
            <div style={{ gridColumn:'1/3', borderTop:'2px solid #E8F5E9', paddingTop:'16px', marginTop:'8px' }}>
              <h4 style={{ color:'#2D5C3F', margin:'0 0 12px' }}>👤 Site Contact</h4>
            </div>
            <div><label style={S.label}>Contact Name</label><input style={S.input} value={facilityForm.site_contact_name} onChange={e => setFacilityForm({...facilityForm, site_contact_name:e.target.value})} /></div>
            <div><label style={S.label}>Contact Phone</label><input style={S.input} value={facilityForm.site_contact_phone} onChange={e => setFacilityForm({...facilityForm, site_contact_phone:e.target.value})} /></div>
            <div style={{ gridColumn:'1/3' }}><label style={S.label}>Contact Email</label><input style={S.input} type="email" value={facilityForm.site_contact_email} onChange={e => setFacilityForm({...facilityForm, site_contact_email:e.target.value})} /></div>
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', marginTop:'24px' }}>
            <button onClick={() => setShowFacilityModal(false)} style={S.btnOutline}>Cancel</button>
            <button onClick={saveFacility} style={S.btn('#2D5C3F','#FFF')}>{editingFacility ? 'Save Changes' : 'Create Facility'}</button>
          </div>
        </div>
      </div>
    );
  }

  function renderProductModal() {
    return (
      <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', justifyContent:'center', alignItems:'center' }}>
        <div style={{ backgroundColor:'#FFF', borderRadius:'12px', width:'95%', maxWidth:'1100px', height:'85vh', display:'flex', flexDirection:'column', overflow:'hidden' }}>
          <div style={{ padding:'20px 24px', borderBottom:'2px solid #E0E0E0', display:'flex', justifyContent:'space-between', alignItems:'center', backgroundColor:'#2D5C3F' }}>
            <div>
              <h2 style={{ margin:0, color:'#FFF', fontSize:'18px' }}>📦 Product Management</h2>
              <p style={{ margin:'4px 0 0', color:'#D4AF37', fontSize:'14px' }}>{selectedFacility?.name}</p>
            </div>
            <div style={{ display:'flex', gap:'10px' }}>
              <button onClick={approveAll} style={{ backgroundColor:'#4CAF50', color:'#FFF', border:'none', padding:'8px 14px', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600' }}>✅ Approve All</button>
              <button onClick={removeAll} style={{ backgroundColor:'#F44336', color:'#FFF', border:'none', padding:'8px 14px', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600' }}>🗑️ Remove All</button>
              <button onClick={() => setShowProductModal(false)} style={{ backgroundColor:'rgba(255,255,255,0.2)', color:'#FFF', border:'1px solid rgba(255,255,255,0.4)', padding:'8px 16px', borderRadius:'6px', cursor:'pointer', fontSize:'14px', fontWeight:'600' }}>✕ Close</button>
            </div>
          </div>
          {productsLoading ? <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center' }}>Loading...</div> : (
            <div style={{ flex:1, display:'flex', overflow:'hidden' }}>
              {/* LEFT - Master */}
              <div style={{ flex:1, display:'flex', flexDirection:'column', borderRight:'2px solid #E0E0E0' }}>
                <div style={{ padding:'16px', backgroundColor:'#F5F5F5', borderBottom:'1px solid #E0E0E0' }}>
                  <h3 style={{ margin:'0 0 10px', fontSize:'14px', color:'#333' }}>🏪 Master Catalog ({filteredMaster.length})</h3>
                  <input type="text" placeholder="Search..." value={searchMaster} onChange={e => setSearchMaster(e.target.value)}
                    style={{ width:'100%', padding:'8px', border:'1px solid #DDD', borderRadius:'6px', fontSize:'13px', marginBottom:'8px', boxSizing:'border-box' }} />
                  <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                    <button onClick={() => setCategoryFilter('all')} style={{ padding:'4px 10px', borderRadius:'12px', fontSize:'11px', cursor:'pointer', fontWeight:'600', border:'none', backgroundColor:categoryFilter==='all'?'#2D5C3F':'#E0E0E0', color:categoryFilter==='all'?'#FFF':'#666' }}>All</button>
                    {pCategories.map(c => <button key={c} onClick={() => setCategoryFilter(c)} style={{ padding:'4px 10px', borderRadius:'12px', fontSize:'11px', cursor:'pointer', fontWeight:'600', border:'none', backgroundColor:categoryFilter===c?getCatColor(c):'#E0E0E0', color:categoryFilter===c?'#FFF':'#666' }}>{c}</button>)}
                  </div>
                  <button onClick={() => setShowNewProduct(!showNewProduct)} style={{ marginTop:'8px', width:'100%', padding:'6px', backgroundColor:'#E8F5E9', color:'#2D5C3F', border:'1px dashed #2D5C3F', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600' }}>+ Add New Product</button>
                </div>
                {showNewProduct && (
                  <div style={{ padding:'12px 16px', backgroundColor:'#FFFDE7', borderBottom:'1px solid #E0E0E0' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px', marginBottom:'8px' }}>
                      <input placeholder="Name *" value={newProduct.name} onChange={e=>setNewProduct({...newProduct,name:e.target.value})} style={{padding:'6px',border:'1px solid #DDD',borderRadius:'4px',fontSize:'12px'}} />
                      <input placeholder="EPA #" value={newProduct.epa_number} onChange={e=>setNewProduct({...newProduct,epa_number:e.target.value})} style={{padding:'6px',border:'1px solid #DDD',borderRadius:'4px',fontSize:'12px'}} />
                      <input placeholder="Active Ingredient" value={newProduct.active_ingredient} onChange={e=>setNewProduct({...newProduct,active_ingredient:e.target.value})} style={{padding:'6px',border:'1px solid #DDD',borderRadius:'4px',fontSize:'12px'}} />
                      <select value={newProduct.category} onChange={e=>setNewProduct({...newProduct,category:e.target.value})} style={{padding:'6px',border:'1px solid #DDD',borderRadius:'4px',fontSize:'12px'}}>
                        <option value="insecticide">Insecticide</option><option value="rodenticide">Rodenticide</option><option value="trap">Trap</option><option value="glue_board">Glue Board</option><option value="dust">Dust</option><option value="granular">Granular</option><option value="other">Other</option>
                      </select>
                    </div>
                    <div style={{display:'flex',gap:'8px'}}><button onClick={createProduct} style={{flex:1,padding:'6px',backgroundColor:'#4CAF50',color:'#FFF',border:'none',borderRadius:'4px',cursor:'pointer',fontSize:'12px',fontWeight:'600'}}>Save</button><button onClick={()=>setShowNewProduct(false)} style={{padding:'6px 12px',backgroundColor:'#EEE',border:'none',borderRadius:'4px',cursor:'pointer',fontSize:'12px'}}>Cancel</button></div>
                  </div>
                )}
                <div style={{ flex:1, overflowY:'auto', padding:'8px' }}>
                  {filteredMaster.length===0 ? <div style={{textAlign:'center',padding:'40px',color:'#999'}}>{searchMaster?'No matches':'All approved ✅'}</div> :
                    filteredMaster.map(p => (
                      <div key={p.id} onClick={() => addProduct(p)} style={{ padding:'10px 12px', margin:'4px 0', border:'1px solid #E0E0E0', borderRadius:'8px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', backgroundColor:'#FFF' }}
                        onMouseEnter={e=>{e.currentTarget.style.backgroundColor='#E8F5E9';e.currentTarget.style.borderColor='#4CAF50';}}
                        onMouseLeave={e=>{e.currentTarget.style.backgroundColor='#FFF';e.currentTarget.style.borderColor='#E0E0E0';}}>
                        <div>
                          <div style={{fontWeight:'600',fontSize:'13px',color:'#333'}}>{p.name}</div>
                          <div style={{fontSize:'11px',color:'#888',marginTop:'2px'}}>{p.epa_number&&`EPA: ${p.epa_number}`}{p.active_ingredient&&` | ${p.active_ingredient}`}</div>
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                          <span style={{padding:'2px 8px',borderRadius:'10px',fontSize:'10px',fontWeight:'600',color:'#FFF',backgroundColor:getCatColor(p.category)}}>{p.category}</span>
                          <span style={{color:'#4CAF50',fontSize:'18px',fontWeight:'bold'}}>→</span>
                        </div>
                      </div>
                    ))
                  }
                </div>
              </div>
              {/* RIGHT - Approved */}
              <div style={{ flex:1, display:'flex', flexDirection:'column' }}>
                <div style={{ padding:'16px', backgroundColor:'#E8F5E9', borderBottom:'1px solid #C8E6C9' }}>
                  <h3 style={{ margin:0, fontSize:'14px', color:'#2D5C3F' }}>✅ Approved ({facilityProducts.length})</h3>
                  <p style={{ margin:'4px 0 0', fontSize:'11px', color:'#666' }}>Products available during inspections</p>
                </div>
                <div style={{ flex:1, overflowY:'auto', padding:'8px' }}>
                  {facilityProducts.length===0 ? <div style={{textAlign:'center',padding:'40px',color:'#999'}}>📦<br/>Click products from the left</div> :
                    facilityProducts.map(p => (
                      <div key={p.product_id||p.id} style={{ padding:'10px 12px', margin:'4px 0', border:'1px solid #C8E6C9', borderRadius:'8px', backgroundColor:'#F1F8E9', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <div>
                          <div style={{fontWeight:'600',fontSize:'13px',color:'#333'}}>{p.name}</div>
                          <div style={{fontSize:'11px',color:'#888',marginTop:'2px'}}>{p.epa_number&&`EPA: ${p.epa_number}`}{p.active_ingredient&&` | ${p.active_ingredient}`}</div>
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                          <span style={{padding:'2px 8px',borderRadius:'10px',fontSize:'10px',fontWeight:'600',color:'#FFF',backgroundColor:getCatColor(p.category)}}>{p.category}</span>
                          <button onClick={()=>removeProduct(p)} style={{backgroundColor:'#FFEBEE',color:'#F44336',border:'1px solid #FFCDD2',borderRadius:'6px',padding:'4px 8px',cursor:'pointer',fontSize:'12px',fontWeight:'600'}}>✕</button>
                        </div>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    );
  }
};

export default ClientsSearch;
